// ===== AWS Backend Configuration =====
// All API calls now go to AWS API Gateway
const AWS_API_BASE_URL_FALLBACK = 'https://uk8q3b8x0a.execute-api.us-east-1.amazonaws.com/dev';
const AWS_API_BASE_URL = (import.meta?.env?.VITE_API_BASE_URL || '').trim() || AWS_API_BASE_URL_FALLBACK;
// Redirect legacy API_BASE_URL to AWS for backward compatibility
const API_BASE_URL = AWS_API_BASE_URL;

// AWS Cognito Authentication
const getAuthToken = () => {
  try {
    const localUser = JSON.parse(localStorage.getItem('user') || '{}');
    const sessionUser = JSON.parse(sessionStorage.getItem('user') || '{}');
    return (
      localUser?.tokens?.idToken ||
      localUser?.idToken ||
      sessionUser?.tokens?.idToken ||
      sessionUser?.idToken ||
      ''
    );
  } catch {
    return '';
  }
};

const hasAuthToken = () => !!getAuthToken();

// API Headers with AWS Cognito token
const getHeaders = () => {
  const token = getAuthToken();
  return {
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    'Content-Type': 'application/json'
  };
};
const hasAwsBackend = () => !!AWS_API_BASE_URL;
const awsUrl = (path) => {
  const base = AWS_API_BASE_URL.replace(/\/+$/, '');
  const p = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
  return `${base}${p}`;
};
const getAwsIdToken = () => {
  try {
    const u = getCurrentUserSession();
    // Try multiple possible locations for the token
    return (
      u?.tokens?.idToken ||
      u?.tokens?.IdToken ||
      u?.idToken ||
      u?.IdToken ||
      ''
    );
  } catch {
    return '';
  }
};
const awsHeaders = ({ auth = true } = {}) => {
  const h = { 'Content-Type': 'application/json' };
  if (auth) {
    const t = getAwsIdToken();
    if (t) {
      h.Authorization = `Bearer ${t}`;
      // Debug: log token presence (but not the token itself for security)
      if (import.meta?.env?.DEV) {
        console.log('[AWS API] Token found, length:', t.length);
      }
    } else {
      console.warn('[AWS API] No ID token found - user may need to log in again');
      // Debug: check what's actually in localStorage
      if (import.meta?.env?.DEV) {
        try {
          const u = getCurrentUserSession();
          console.log('[AWS API] Current user session keys:', u ? Object.keys(u) : 'no user');
          console.log('[AWS API] Has tokens?', !!u?.tokens);
          console.log('[AWS API] Has idToken?', !!u?.idToken);
        } catch (e) {
          console.error('[AWS API] Error checking user session:', e);
        }
      }
    }
  }
  return h;
};
const awsFetchJson = async (path, { method = 'GET', body, auth = true } = {}) => {
  // Check for token before making request if auth is required
  if (auth) {
    const token = getAwsIdToken();
    if (!token) {
      const err = new Error('Authentication required. Please log out and log back in to refresh your session.');
      err.status = 401;
      err.needsReauth = true;
      throw err;
    }
  }

  const res = await fetch(awsUrl(path), {
    method,
    headers: awsHeaders({ auth }),
    body: body ? JSON.stringify(body) : undefined
  });
  let parsed = null;
  try {
    parsed = await res.json();
  } catch {
    parsed = null;
  }
  if (!res.ok) {
    let msg = parsed?.error || parsed?.message || `API Error: ${res.status} ${res.statusText}`;
    
    // Provide helpful message for 403 (Forbidden) - usually means expired/invalid token
    if (res.status === 403) {
      // Log diagnostic info in dev mode
      if (import.meta?.env?.DEV) {
        const token = getAwsIdToken();
        console.error('[AWS API] 403 Forbidden - Token info:', {
          hasToken: !!token,
          tokenLength: token?.length || 0,
          tokenPrefix: token ? token.substring(0, 20) + '...' : 'none',
          path,
          method
        });
      }
      msg = 'Session expired or invalid token. Please log out and log back in to refresh your authentication.';
    } else if (res.status === 401) {
      msg = 'Authentication required. Please log in again.';
    }
    
    const err = new Error(msg);
    err.status = res.status;
    if (res.status === 403 || res.status === 401) {
      err.needsReauth = true;
    }
    throw err;
  }
  return parsed;
};

/**
 * Upload image to S3 via presigned URL
 * @param {File} file - The image file to upload
 * @param {string} imageType - Either 'avatar' or 'banner'
 * @returns {Promise<string>} - The CloudFront URL of the uploaded image
 */
export const uploadImageToS3 = async (file, imageType) => {
  if (!file) {
    throw new Error('No file provided for upload');
  }
  
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }
  
  try {
    // 1. Get presigned URL from backend
    const presignData = await awsFetchJson('/uploads/presign', {
      method: 'POST',
      body: { imageType, contentType: file.type },
      auth: true
    });
    
    if (!presignData?.putUrl || !presignData?.publicUrl) {
      throw new Error('Invalid presign response from server');
    }
    
    // Check for storage warnings
    if (presignData.storage_warning) {
      console.warn('[Upload] Storage warning:', presignData.storage_warning);
      // You can optionally show a toast/notification here
    }
    
    // 2. Upload file directly to S3 using presigned URL
    const uploadResponse = await fetch(presignData.putUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file
    });
    
    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload to S3: ${uploadResponse.status} ${uploadResponse.statusText}`);
    }
    
    // 3. Return CloudFront URL for storage in DynamoDB
    return presignData.publicUrl;
  } catch (error) {
    // Handle storage limit errors with user-friendly messages
    if (error.message?.includes('Storage limit exceeded')) {
      throw new Error('You have reached your 2GB storage limit. Please delete some files to upload new ones.');
    }
    throw error;
  }
};

/**
 * Upload any file (portfolio docs, PDFs, etc.) to S3 via presigned URL.
 * Unlike uploadImageToS3, this does NOT enforce image MIME types.
 * @param {File} file - The File to upload
 * @param {string} fileType - The imageType for the presign endpoint (default: 'portfolio')
 * @returns {Promise<string>} - The CloudFront public URL of the uploaded file
 */
export const uploadFileToS3 = async (file, fileType = 'portfolio') => {
  if (!file) {
    throw new Error('No file provided for upload');
  }

  const ext = (file.name || '').split('.').pop().toLowerCase();

  try {
    const presignData = await awsFetchJson('/uploads/presign', {
      method: 'POST',
      body: { imageType: fileType, contentType: file.type || 'application/octet-stream', ext },
      auth: true
    });

    if (!presignData?.putUrl || !presignData?.publicUrl) {
      throw new Error('Invalid presign response from server');
    }

    if (presignData.storage_warning) {
      console.warn('[Upload] Storage warning:', presignData.storage_warning);
    }

    const uploadResponse = await fetch(presignData.putUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file
    });

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload to S3: ${uploadResponse.status} ${uploadResponse.statusText}`);
    }

    return presignData.publicUrl;
  } catch (error) {
    if (error.message?.includes('Storage limit exceeded')) {
      throw new Error('You have reached your 2GB storage limit. Please delete some files to upload new ones.');
    }
    throw error;
  }
};

const fetchAwsProfilePublicLinkId = async () => {
  try {
    if (!hasAwsBackend()) return null;
    const data = await awsFetchJson('/me/profile', { auth: true });
    return data?.profile?.public_link_id || null;
  } catch {
    return null;
  }
};

const fetchAwsMyProfile = async () => {
  try {
    if (!hasAwsBackend()) return null;
    const data = await awsFetchJson('/me/profile', { auth: true });
    return data?.profile || null;
  } catch {
    return null;
  }
};

/**
 * Convert Users table profile data to ProfilePage format
 * NO resume data - only Users table fields
 */
const convertUsersTableProfileToResumeFormat = (profile) => {
  if (!profile || typeof profile !== 'object') return null;
  
  return {
    // IDs
    id: profile.user_id || profile.public_link_id,
    _id: profile.user_id || profile.public_link_id,
    user_id: profile.user_id,
    owner_user_id: profile.user_id,
    public_link_id: profile.public_link_id,
    profile_slug: profile.profile_slug || profile.profileSlug || '',
    is_public: profile.is_public !== false,
    
    // Profile fields from Users table ONLY
    title: profile.name || '',
    headline: profile.headline || '',
    summary: profile.about || profile.summary || '',
    professional_summary: profile.professionalSummary || profile.professional_summary || '',
    
    // Images from Users table
    photo_url: profile.profilePictureKey || profile.photo_url || '',
    banner_url: profile.bannerPictureKey || profile.banner_url || profile.bannerUrl || '',
    
    // Contact info from Users table
    contact_email: profile.email || profile.contact?.email || '',
    contact_phone: profile.contact?.phone || profile.phone || '',
    location: profile.location || '',
    linkedin_url: profile.linkedin_url || profile.linkedinUrl || profile.contact?.linkedin || '',
    website_url: profile.website_url || profile.websiteUrl || profile.contact?.website || '',
    
    // Profile settings from Users table
    credentials: profile.credentials || '',
    pronouns: profile.pronouns || '',
    highlight_color: profile.highlight_color || profile.highlightColor || '#3b82f6',
    profile_frame: profile.profile_frame || profile.profileFrame || '',
    target_role: profile.targetRole || profile.target_role || '',
    
    // Users table only stores basic profile fields - NO experience/education (those are in Resumes table)
    // Set empty arrays for fields that ProfilePage expects but aren't in Users table
    experience: [],
    education: [],
    skills: [],
    projects: [],
    certifications: [],
    languages: [],
    training: [],
    credly_badges: [],
    core_skills: [],
    is_primary: true
  };
};

const convertPublicProfileToResumeFormat = (profile) => {
  if (!profile || typeof profile !== 'object') return null;
  const safeParse = (val, defaultVal = []) => {
    if (!val) return defaultVal;
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : defaultVal;
      } catch {
        return defaultVal;
      }
    }
    return defaultVal;
  };

  return {
    // IDs
    id: profile.user_id || profile.public_link_id || profile.profile_slug,
    _id: profile.user_id || profile.public_link_id || profile.profile_slug,
    user_id: profile.user_id,
    owner_user_id: profile.user_id,
    public_link_id: profile.public_link_id,
    profile_slug: profile.profile_slug || profile.profileSlug || '',
    is_public: profile.is_public !== false,

    // Profile fields
    title: profile.name || '',
    headline: profile.headline || '',
    summary: profile.about || profile.summary || '',
    professional_summary: profile.professionalSummary || profile.professional_summary || '',

    // Images
    photo_url: profile.profilePictureKey || profile.photo_url || '',
    banner_url: profile.bannerPictureKey || profile.banner_url || profile.bannerUrl || '',

    // Contact info
    contact_email: profile.email || profile.contact?.email || '',
    contact_phone: profile.contact?.phone || profile.phone || '',
    location: profile.location || '',
    linkedin_url: profile.linkedin_url || profile.linkedinUrl || profile.contact?.linkedin || '',
    website_url: profile.website_url || profile.websiteUrl || profile.contact?.website || '',

    // Profile settings
    credentials: profile.credentials || '',
    pronouns: profile.pronouns || '',
    highlight_color: profile.highlight_color || profile.highlightColor || '#3b82f6',
    profile_frame: profile.profile_frame || profile.profileFrame || '',
    target_role: profile.targetRole || profile.target_role || '',

    // Rich content
    experience: safeParse(profile.experience || profile.work_experience || profile.workExperience, []),
    education: safeParse(profile.education, []),
    skills: safeParse(profile.skills, []),
    projects: safeParse(profile.projects, []),
    certifications: safeParse(profile.certifications, []),
    languages: safeParse(profile.languages, []),
    training: safeParse(profile.training, []),
    credly_badges: safeParse(profile.credly_badges, []),
    core_skills: safeParse(profile.core_skills, []),
    is_primary: true
  };
};

/**
 * PRODUCTION SAFETY NOTES
 * - We never delete/reset global data stores.
 * - We scope writes to a single user's user_id key.
 * - Legacy global storage ("localResumes") is read-only and only used as a migration source.
 */

// Current session helpers (immutable user_id is primary identifier)
const getCurrentUserSession = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const getCurrentUserId = () => {
  const u = getCurrentUserSession();
  return u?.user_id || u?.userId || u?.id || u?._id || null;
};

const getUserScopedResumesKey = (userId) => `localResumes:${userId}`;
const getUserScopedResumesBackupKey = (userId) => `localResumesBackup:${userId}`;
const getUserScopedResumesTombstonesKey = (userId) => `localResumesTombstones:${userId}`;

// Legacy Local Storage Resume Management (READ ONLY, migration source)
const getLegacyLocalResumes = () => {
  try {
    const resumes = localStorage.getItem('localResumes');
    return resumes ? JSON.parse(resumes) : [];
  } catch (e) {
    console.error("Error reading local resumes from localStorage", e);
    return [];
  }
};

// User-scoped local storage (WRITE ONLY to user key)
const getLocalResumesForUser = (userId) => {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(getUserScopedResumesKey(userId));
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error('Error reading user-scoped resumes', e);
    return [];
  }
};

const saveLocalResumesForUser = (userId, resumes) => {
  if (!userId) return;
  try {
    localStorage.setItem(getUserScopedResumesKey(userId), JSON.stringify(resumes));
  } catch (e) {
    console.error('Error saving user-scoped resumes', e);
  }
};

const getResumeTombstonesForUser = (userId) => {
  if (!userId) return { ids: [], publicLinks: [] };
  try {
    const raw = localStorage.getItem(getUserScopedResumesTombstonesKey(userId));
    const obj = raw ? JSON.parse(raw) : null;
    const ids = Array.isArray(obj?.ids) ? obj.ids.filter(Boolean).map(String) : [];
    const publicLinks = Array.isArray(obj?.publicLinks) ? obj.publicLinks.filter(Boolean).map(String) : [];
    return { ids, publicLinks };
  } catch {
    return { ids: [], publicLinks: [] };
  }
};

const saveResumeTombstonesForUser = (userId, tombstones) => {
  if (!userId) return;
  try {
    const payload = {
      ids: Array.isArray(tombstones?.ids) ? tombstones.ids.slice(0, 500) : [],
      publicLinks: Array.isArray(tombstones?.publicLinks) ? tombstones.publicLinks.slice(0, 500) : []
    };
    localStorage.setItem(getUserScopedResumesTombstonesKey(userId), JSON.stringify(payload));
  } catch {
    // ignore
  }
};

const addResumeTombstoneForUser = (userId, { id, public_link_id } = {}) => {
  if (!userId) return;
  const cur = getResumeTombstonesForUser(userId);
  const ids = new Set(cur.ids);
  const publicLinks = new Set(cur.publicLinks);
  if (id) {
    ids.add(String(id));
    console.log('[Tombstone] Added ID:', String(id));
  }
  if (public_link_id) {
    publicLinks.add(String(public_link_id));
    console.log('[Tombstone] Added public link:', String(public_link_id));
  }
  saveResumeTombstonesForUser(userId, { ids: Array.from(ids), publicLinks: Array.from(publicLinks) });
  console.log('[Tombstone] Total tombstones:', ids.size, 'IDs,', publicLinks.size, 'public links');
};

const isResumeTombstonedForUser = (userId, resumeOrId) => {
  if (!userId) {
    console.warn('[Tombstone Check] ⚠️ No userId provided - cannot check tombstones!');
    return false;
  }
  const { ids, publicLinks } = getResumeTombstonesForUser(userId);
  const idSet = new Set(ids);
  const pidSet = new Set(publicLinks);
  
  console.log('[Tombstone Check] Checking resume. Current tombstones:', { ids: Array.from(idSet), publicLinks: Array.from(pidSet) });
  
  if (typeof resumeOrId === 'string' || typeof resumeOrId === 'number') {
    const isTombstoned = idSet.has(String(resumeOrId));
    console.log('[Tombstone Check] String/Number check:', resumeOrId, '→', isTombstoned ? '✅ IS tombstoned' : '❌ NOT tombstoned');
    return isTombstoned;
  }
  const r = resumeOrId;
  const rid = r?.id || r?._id || r?.resume_id || r?.resumeId;
  const pid = r?.public_link_id;
  
  console.log('[Tombstone Check] Checking resume object:', { id: rid, public_link_id: pid });
  
  if (rid && idSet.has(String(rid))) {
    console.log('[Tombstone Check] ✅ Resume', rid, 'IS tombstoned (matched by ID)');
    return true;
  }
  if (pid && pidSet.has(String(pid))) {
    console.log('[Tombstone Check] ✅ Resume', rid || 'unknown', 'IS tombstoned (matched by public_link_id:', pid + ')');
    return true;
  }
  
  // Not tombstoned - log detailed info
  console.log('[Tombstone Check] ❌ Resume', rid || 'unknown', 'NOT tombstoned');
  console.log('[Tombstone Check] Resume ID:', rid, 'not in tombstone IDs:', Array.from(idSet));
  console.log('[Tombstone Check] Resume public_link:', pid, 'not in tombstone public_links:', Array.from(pidSet));
  return false;
};

// Non-destructive, reversible backup (per-user)
const backupLocalResumesForUser = (userId, reason = 'backup') => {
  if (!userId) return null;
  try {
    const current = getLocalResumesForUser(userId);
    const backup = {
      at: new Date().toISOString(),
      reason,
      resumes: current
    };
    localStorage.setItem(getUserScopedResumesBackupKey(userId), JSON.stringify(backup));
    return backup.at;
  } catch (e) {
    console.error('Error backing up user-scoped resumes', e);
    return null;
  }
};

// Safe merge: never overwrite non-empty existing data
const isEmpty = (v) => v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
const isEmptyArray = (v) => !Array.isArray(v) || v.length === 0;

const normalizeEmail = (email) => (email ? String(email).trim().toLowerCase() : '');
const resumeMatchesEmail = (resume, email) => {
  const target = normalizeEmail(email);
  if (!target || !resume) return false;
  const fields = [
    resume.contact_email,
    resume.contactEmail,
    resume.user_email,
    resume.userEmail,
    resume.email,
    resume.owner_email,
    resume.ownerEmail
  ].filter(Boolean);
  return fields.map(normalizeEmail).includes(target);
};

// ===== Resume selection helpers (SELECT-only consumers) =====
const resumeGetArr = (r, keys) => {
  for (const k of keys) {
    const v = r?.[k];
    if (Array.isArray(v)) return v;
  }
  return [];
};

const resumeByTs = (r) => {
  const raw =
    r?.updated_at ??
    r?.updatedAt ??
    r?.created_at ??
    r?.createdAt ??
    r?.updated ??
    r?.created;
  const t = raw ? Date.parse(raw) : NaN;
  return Number.isFinite(t) ? t : 0;
};

// Heuristic: prefer records that look like a "complete" profile.
const resumeCompletenessScore = (r) => {
  if (!r) return 0;
  const exp = resumeGetArr(r, ['experience', 'work_experience', 'workExperience']);
  const edu = resumeGetArr(r, ['education', 'educations']);
  const skills = resumeGetArr(r, ['skills']);
  const certs = resumeGetArr(r, ['certifications', 'licenses', 'license_or_certification']);
  const langs = resumeGetArr(r, ['languages']);
  const projects = resumeGetArr(r, ['projects']);

  const str = (v) => (typeof v === 'string' ? v.trim() : '');
  const score =
    (r?.is_primary === true ? 50 : 0) +
    Math.min(exp.length, 12) * 10 +
    Math.min(edu.length, 6) * 6 +
    Math.min(projects.length, 6) * 4 +
    Math.min(certs.length, 10) * 3 +
    Math.min(langs.length, 10) * 1 +
    Math.min(skills.length, 50) * 0.5 +
    Math.min(str(r?.summary || r?.about || r?.about_text).length, 600) / 20 +
    Math.min(str(r?.professional_summary || r?.professionalSummary).length, 600) / 20 +
    (str(r?.headline).length > 0 ? 6 : 0) +
    (str(r?.photo_url || r?.photoUrl || r?.photo).length > 0 ? 6 : 0) +
    (str(r?.banner_url || r?.bannerUrl || r?.banner).length > 0 ? 2 : 0);
  return Number.isFinite(score) ? score : 0;
};

const pickBestResumeRecord = (arr) => {
  const list = Array.isArray(arr) ? arr.filter(Boolean) : [];
  if (list.length === 0) return null;
  return list
    .sort((a, b) => {
      const p = (b?.is_primary === true) - (a?.is_primary === true);
      if (p !== 0) return p;
      const c = resumeCompletenessScore(b) - resumeCompletenessScore(a);
      if (c !== 0) return c;
      return resumeByTs(b) - resumeByTs(a);
    })[0] || null;
};

const normalizeResumeForFullView = (r) => {
  if (!r || typeof r !== 'object') return r;

  const safeJsonParse = (value) => {
    if (!value) return null;
    if (typeof value !== 'string') return null;
    const s = value.trim();
    if (!s) return null;
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  };

  // Base44 schema-compat fields:
  // If Base44 doesn't support array/object fields for Resume, we store JSON strings
  // (experience_json, education_json, etc.) and rehydrate them here.
  const profileJson =
    safeJsonParse(r.profile_json) ||
    safeJsonParse(r.resume_json) ||
    safeJsonParse(r.full_profile_json) ||
    null;

  const pickArray = (...vals) => {
    for (const v of vals) {
      if (Array.isArray(v)) return v;
    }
    return [];
  };

  const experience = pickArray(
    r.experience,
    r.work_experience,
    r.workExperience,
    safeJsonParse(r.experience_json),
    safeJsonParse(r.work_experience_json),
    profileJson?.experience,
    profileJson?.work_experience,
    profileJson?.workExperience
  );
  const photo_url = r.photo_url || r.photoUrl || r.photo || '';
  const banner_url = r.banner_url || r.bannerUrl || r.banner || '';
  const contact_email = r.contact_email || r.contactEmail || r.user_email || r.userEmail || r.email || '';
  const contact_phone = r.contact_phone || r.contactPhone || r.phone || '';
  const linkedin_url = r.linkedin_url || r.linkedinUrl || r.linkedin || '';
  const website_url = r.website_url || r.websiteUrl || r.website || '';
  const location = r.location || r.locationText || '';
  const target_role = r.target_role || r.targetRole || '';
  return {
    ...r,
    summary: r.summary || r.about || r.about_text || '',
    professional_summary: r.professional_summary || r.professionalSummary || '',
    photo_url,
    banner_url,
    contact_email,
    contact_phone,
    linkedin_url,
    website_url,
    location,
    target_role,
    experience,
    education: pickArray(
      r.education,
      r.educations,
      safeJsonParse(r.education_json),
      profileJson?.education,
      profileJson?.educations
    ),
    training: pickArray(
      r.training,
      r.professional_development,
      safeJsonParse(r.training_json),
      safeJsonParse(r.professional_development_json),
      profileJson?.training,
      profileJson?.professional_development
    ),
    certifications: pickArray(
      r.certifications,
      r.licenses,
      r.license_or_certification,
      safeJsonParse(r.certifications_json),
      safeJsonParse(r.licenses_json),
      profileJson?.certifications,
      profileJson?.licenses,
      profileJson?.license_or_certification
    ),
    languages: pickArray(
      r.languages,
      r.language,
      safeJsonParse(r.languages_json),
      profileJson?.languages,
      profileJson?.language
    ),
    core_skills: pickArray(
      r.core_skills,
      safeJsonParse(r.core_skills_json),
      profileJson?.core_skills
    ),
    skills: pickArray(
      r.skills,
      safeJsonParse(r.skills_json),
      profileJson?.skills
    ),
    projects: pickArray(
      r.projects,
      safeJsonParse(r.projects_json),
      profileJson?.projects
    )
  };
};

// Pick the best record, then merge missing fields from the rest (fill-empty-only).
const pickAndMergeBestResume = (arr) => {
  const list = Array.isArray(arr) ? arr.filter(Boolean) : [];
  const best = pickBestResumeRecord(list);
  if (!best) return null;
  const bestId = String(best?.id || best?._id || '');
  const others = list
    .filter(r => String(r?.id || r?._id || '') !== bestId)
    .sort((a, b) => {
      const p = (b?.is_primary === true) - (a?.is_primary === true);
      if (p !== 0) return p;
      const c = resumeCompletenessScore(b) - resumeCompletenessScore(a);
      if (c !== 0) return c;
      return resumeByTs(b) - resumeByTs(a);
    });
  let merged = { ...best };
  for (const r of others) {
    merged = safeMergeResume(merged, r);
  }
  return merged;
};

const PROTECTED_EMAILS_NO_AUTOMATION = new Set(['benjamin.maderajr@gmail.com']);

const safeMergeResume = (existing, incoming) => {
  if (!existing) return incoming;
  if (!incoming) return existing;

  const merged = { ...existing };
  for (const [k, v] of Object.entries(incoming)) {
    if (k === 'owner_user_id') continue; // immutable in our local store
    if (Array.isArray(v)) {
      if (isEmptyArray(merged[k]) && !isEmptyArray(v)) merged[k] = v;
    } else if (typeof v === 'object' && v !== null) {
      // shallow object merge only when existing missing
      if (merged[k] == null) merged[k] = v;
    } else {
      if (isEmpty(merged[k]) && !isEmpty(v)) merged[k] = v;
    }
  }
  return merged;
};

const getLocalResumeByIdForUser = (userId, id) => {
  const resumes = getLocalResumesForUser(userId);
  return resumes.find(r => (r.id === id || r._id === id));
};

/**
 * Merge helper: ALWAYS prefer API data when it is defined.
 * The API (DynamoDB) is the source of truth. localStorage is only a fallback
 * for when the API is unreachable or doesn't have the field at all.
 * This ensures deletes stick (API has fewer items) and saved strings persist.
 */
const mergePreferContent = (apiVal, localVal, fallback) => {
  const aIsArr = Array.isArray(apiVal);
  const lIsArr = Array.isArray(localVal);
  if (aIsArr || lIsArr) {
    if (aIsArr) return apiVal;        // API has array data — use it (even if empty)
    if (lIsArr) return localVal;      // API undefined, fall back to localStorage
    return fallback || [];
  }
  // Strings: prefer API when defined (even empty string), else localStorage
  if (apiVal !== undefined && apiVal !== null) return apiVal;
  if (localVal !== undefined && localVal !== null) return localVal;
  return fallback ?? '';
};

const updateLocalResumeForUser = (userId, id, updateData) => {
  const resumes = getLocalResumesForUser(userId);
  const index = resumes.findIndex(r => (r.id === id || r._id === id));
  if (index !== -1) {
    const existing = resumes[index];
    // Prevent cross-account association changes:
    // owner_user_id is immutable once set.
    const owner_user_id = existing.owner_user_id || updateData.owner_user_id;
    resumes[index] = {
      ...existing,
      ...updateData,
      owner_user_id,
      id: existing.id || existing._id || id
    };
    saveLocalResumesForUser(userId, resumes);
    return resumes[index];
  }
  throw new Error('Resume not found in localStorage');
};

const createLocalResumeForUser = (userId, resumeData) => {
  const resumes = getLocalResumesForUser(userId);
  const newResume = {
    ...resumeData,
    owner_user_id: resumeData.owner_user_id || userId,
    id: resumeData.id || Date.now().toString(),
    _id: resumeData._id || resumeData.id || Date.now().toString(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
  resumes.push(newResume);
  saveLocalResumesForUser(userId, resumes);
  return newResume;
};

// Resume API Service
export const resumeAPI = {
  // Export normalizeResumeForFullView for use in ProfilePage
  normalizeResumeForFullView,
  // Export S3 upload helpers
  uploadImageToS3,
  uploadFileToS3,
  async importResumeDocx({ docxBase64, template } = {}) {
    if (!hasAwsBackend()) {
      throw new Error('AWS backend not configured');
    }
    const payload = {
      docx_base64: docxBase64 || '',
      template: template || ''
    };
    return await awsFetchJson('/me/resumes/import-docx', {
      method: 'POST',
      auth: true,
      body: payload
    });
  },
  async exportResumePdf(resumeId, { template, useCache = true } = {}) {
    if (!hasAwsBackend()) {
      throw new Error('AWS backend not configured');
    }
    const rid = String(resumeId || '').trim();
    if (!rid) throw new Error('Resume ID is required');
    return await awsFetchJson(`/me/resumes/${encodeURIComponent(rid)}/export/pdf`, {
      method: 'POST',
      auth: true,
      body: {
        template: template || '',
        use_cache: useCache !== false
      }
    });
  },
  async fetchResumeExport(resumeId, exportToken) {
    const rid = String(resumeId || '').trim();
    if (!rid) throw new Error('Resume ID is required');
    if (!exportToken) throw new Error('Export token is required');
    return await awsFetchJson(
      `/public/resume-export/${encodeURIComponent(rid)}?token=${encodeURIComponent(exportToken)}`,
      { auth: false }
    );
  },
  async getMyProfile() {
    return await fetchAwsMyProfile();
  },
  async updateMyProfile(patch) {
    if (!hasAwsBackend()) {
      throw new Error('AWS backend not configured');
    }
    const data = await awsFetchJson('/me/profile', {
      method: 'PUT',
      auth: true,
      body: patch || {}
    });
    return data?.profile || null;
  },
  /**
   * Pure helper: given a list of resumes, pick the best and fill missing fields
   * from the remaining resumes (fill-empty-only). No network, no writes.
   */
  pickBestResumeFromList(resumes, { includeFull = true } = {}) {
    const merged = pickAndMergeBestResume(resumes);
    if (!merged) return null;
    const base = {
      id: merged.id || merged._id,
      profile_user_id: merged.owner_user_id || merged.user_id || null,
      public_link_id: merged.public_link_id || null,
      title: merged.title,
      headline: merged.headline,
      target_role: merged.target_role || merged.targetRole,
      photo_url: merged.photo_url || merged.photoUrl
    };
    if (!includeFull) return base;
    return normalizeResumeForFullView({ ...merged, ...base });
  },

  /**
   * SELECT-only: get a reliable resume list for the CURRENT user without mutating anything.
   * Used by My Resumes to avoid "missing resumes" caused by tombstones/id mismatches.
   */
  async fetchAllBestEffortForCurrentUser() {
    const user = getCurrentUserSession();
    const userId = getCurrentUserId();
    const email = user?.email || null;
    if (!userId) return [];

    // If AWS backend is configured, use the AWS-backed fetchAll path.
    if (hasAwsBackend()) {
      return await this.fetchAll({});
    }

    const parseList = async (response) => {
      const data = await response.json();
      if (Array.isArray(data)) return data;
      if (data?.data && Array.isArray(data.data)) return data.data;
      if (data?.results && Array.isArray(data.results)) return data.results;
      if (data?.items && Array.isArray(data.items)) return data.items;
      return data ? [data] : [];
    };

    const tryFetch = async (filters) => {
      const query = filters ? new URLSearchParams(filters).toString() : '';
      const url = `${API_BASE_URL}/me/resumes${query ? `?${query}` : ''}`;
      const res = await fetch(url, { headers: getHeaders() });
      if (!res.ok) return [];
      return await parseList(res);
    };

    // Prefer owner_user_id / user_id scoped fetch.
    let api = await tryFetch({ owner_user_id: userId });
    if (api.length === 0) api = await tryFetch({ user_id: userId });

    // If the backend ignores filters or data is legacy, fallback to email-based lookup.
    if (api.length === 0 && email) {
      const e = String(email).trim();
      api = [
        ...await tryFetch({ contact_email: e }),
        ...await tryFetch({ contactEmail: e }),
        ...await tryFetch({ user_email: e }),
        ...await tryFetch({ userEmail: e }),
        ...await tryFetch({ email: e }),
        ...await tryFetch({ owner_email: e }),
        ...await tryFetch({ ownerEmail: e })
      ];
    }

    // Last resort: fetch all and filter client-side.
    if (api.length === 0) {
      api = await tryFetch(null);
    }

    const apiOwned = (Array.isArray(api) ? api : []).filter(r => {
      if (!r) return false;
      if (r?.owner_user_id) return String(r.owner_user_id) === String(userId);
      if (r?.user_id) return String(r.user_id) === String(userId);
      return !!email && resumeMatchesEmail(r, email);
    });

    // Merge in local user-scoped resumes (no migration).
    const local = getLocalResumesForUser(userId);
    const merged = [...apiOwned];
    for (const lr of local) {
      const lid = String(lr?.id || lr?._id || '');
      if (!lid) continue;
      const exists = merged.some(r => String(r?.id || r?._id || '') === lid);
      if (!exists) merged.push(lr);
    }

    // CRITICAL: Deduplicate before tombstone filter
    console.log('[fetchAllBestEffort] BEFORE deduplication:', merged.length);
    const seenIds = new Set();
    const deduplicated = [];
    for (const r of merged) {
      const uniqueKey = String(r?.id || r?._id || '');
      if (!uniqueKey) continue;
      if (seenIds.has(uniqueKey)) {
        console.warn('[fetchAllBestEffort] ⚠️ DUPLICATE! Skipping:', uniqueKey);
        continue;
      }
      seenIds.add(uniqueKey);
      deduplicated.push(r);
    }
    console.log('[fetchAllBestEffort] AFTER deduplication:', deduplicated.length);

    // Apply tombstone filter - trust user's delete actions!
    const withoutTombstones = deduplicated.filter(r => !isResumeTombstonedForUser(userId, r));
    console.log('[fetchAllBestEffort] Before tombstone:', deduplicated.length, 'After tombstone:', withoutTombstones.length);
    // REMOVED SAFETY: User explicitly deleted these, return what they want!
    return withoutTombstones;
  },
  /**
   * Per-user, non-destructive migration:
   * - Reads legacy global 'localResumes' and copies ONLY the current user's records
   *   into 'localResumes:<user_id>'.
   * - Does NOT delete or modify legacy storage.
   */

  /**
   * Fetch all resume entities
   * Filterable fields: title, headline, summary, target_role, target_industry,
   * seniority_level, photo_url, banner_url, contact_email, contact_phone,
   * location, linkedin_url, website_url, ai_score, ai_feedback, is_primary,
   * template, public_link_id
   */
  async fetchAll(filters = {}) {
    // Private API: always scoped to current user_id
    const userId = getCurrentUserId();
    const user = getCurrentUserSession();
    const email = user?.email || null;
    if (!userId) {
      // Not authenticated; do not leak any data
      return [];
    }

    // Preferred: AWS backend (Cognito + API)
    if (hasAwsBackend()) {
      try {
        const url = filters.full ? '/me/resumes?full=true' : '/me/resumes';
        const data = await awsFetchJson(url, { auth: true });
        const items = Array.isArray(data?.resumes) ? data.resumes : [];
        const apiResumes = items.map((it) => {
          const rid = it?.resume_id || it?.resumeId || it?.id || it?._id || null;
          const resumeObj = (it?.resume && typeof it.resume === 'object') ? it.resume : null;
          const flat = { ...(resumeObj || {}), ...(it || {}) };
          delete flat.resume;
          delete flat.resume_id;
          delete flat.resumeId;
          if (rid) {
            flat.id = flat.id || rid;
            flat._id = flat._id || rid;
          }
          flat.owner_user_id = flat.owner_user_id || userId;
          flat.created_at = flat.created_at || flat.createdAt || flat.created;
          flat.updated_at = flat.updated_at || flat.updatedAt || flat.updated;
          return flat;
        });

        const localResumes = getLocalResumesForUser(userId);
        const merged = [...apiResumes];
        for (const lr of localResumes) {
          const lid = String(lr?.id || lr?._id || '');
          if (!lid) continue;
          const exists = merged.some(r => String(r?.id || r?._id || '') === lid);
          if (!exists) merged.push(lr);
        }

        let filtered = merged;
        if (filters.is_primary !== undefined) {
          filtered = filtered.filter(r => r.is_primary === (filters.is_primary === 'true' || filters.is_primary === true));
        }
        if (filters.public_link_id) {
          filtered = filtered.filter(r => r.public_link_id === filters.public_link_id);
        }
        
        // 🔥 CRITICAL: Filter tombstones in AWS path!
        console.log('[fetchAll AWS] BEFORE tombstone filter:', filtered.length);
        console.log('[fetchAll AWS] Filtering tombstones for userId:', userId);
        const beforeTombstone = filtered.length;
        filtered = filtered.filter(r => !isResumeTombstonedForUser(userId, r));
        console.log('[fetchAll AWS] AFTER tombstone filter:', filtered.length, '(removed', beforeTombstone - filtered.length, ')');
        
        return filtered;
      } catch (e) {
        console.warn('AWS fetchAll failed; falling back to local/Base44', e);
      }
    }

    // If Base44 is not configured, fall back to local-only data for the current user.
    if (!hasBase44Key()) {
      const localResumes = getLocalResumesForUser(userId);
      let filtered = localResumes;
      if (filters.is_primary !== undefined) {
        filtered = filtered.filter(r => r.is_primary === (filters.is_primary === 'true' || filters.is_primary === true));
      }
      if (filters.public_link_id) {
        filtered = filtered.filter(r => r.public_link_id === filters.public_link_id);
      }
      return filtered;
    }

    try {
      const queryParams = new URLSearchParams(filters).toString();
      const url = `${API_BASE_URL}/me/resumes${queryParams ? `?${queryParams}` : ''}`;

      const response = await fetch(url, {
        headers: getHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || '';
        
        // If Resume entity doesn't exist or has issues, fall back to localStorage
        if (errorMessage.includes('Entity schema') || 
            errorMessage.includes('not found in app') ||
            response.status === 404) {
          console.warn('Resume entity not found in Base44, using localStorage fallback');
          const localResumes = getLocalResumesForUser(userId);
          let filtered = localResumes;
          if (filters.is_primary !== undefined) {
            filtered = filtered.filter(r => r.is_primary === (filters.is_primary === 'true' || filters.is_primary === true));
          }
          if (filters.public_link_id) {
            filtered = filtered.filter(r => r.public_link_id === filters.public_link_id);
          }
          // CRITICAL: Filter tombstones in fallback path!
          console.log('[Fetch Fallback 404] Filtering tombstones...');
          filtered = filtered.filter(r => !isResumeTombstonedForUser(userId, r));
          return filtered;
        }
        
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      // Handle different response formats
      let apiResumes = [];
      if (Array.isArray(data)) {
        apiResumes = data;
      } else if (data && Array.isArray(data.data)) {
        apiResumes = data.data;
      } else if (data && Array.isArray(data.results)) {
        apiResumes = data.results;
      } else if (data && Array.isArray(data.items)) {
        apiResumes = data.items;
      } else if (data) {
        apiResumes = [data];
      }
      
      // Merge with localStorage resumes to preserve fields like pronouns, credentials, and professional_summary
      // Strict isolation: only include current user's records
      const apiOwnedByUser = apiResumes.filter(r => {
        if (r?.owner_user_id) return r.owner_user_id === userId;
        if (r?.user_id) return r.user_id === userId;
        // Legacy fallback ONLY while migrating existing records
        return !!email && resumeMatchesEmail(r, email);
      });

      const localResumes = getLocalResumesForUser(userId);
      const mergedResumes = apiOwnedByUser.map(apiResume => {
        const localResume = localResumes.find(lr =>
          (lr.id === apiResume.id || lr._id === apiResume._id || lr.id === apiResume._id)
        );
        if (localResume) {
          // Merge: prefer whichever source has more content to prevent stale localStorage from overwriting API data
          return {
            ...apiResume,
            summary: mergePreferContent(apiResume.summary, localResume.summary, ''),
            professional_summary: mergePreferContent(apiResume.professional_summary, localResume.professional_summary, ''),
            photo_url: mergePreferContent(apiResume.photo_url, localResume.photo_url, ''),
            banner_url: mergePreferContent(apiResume.banner_url, localResume.banner_url, ''),
            pronouns: mergePreferContent(apiResume.pronouns, localResume.pronouns, ''),
            credentials: mergePreferContent(apiResume.credentials, localResume.credentials, ''),
            profile_frame: mergePreferContent(apiResume.profile_frame, localResume.profile_frame, ''),
            headline: mergePreferContent(apiResume.headline, localResume.headline, ''),
            target_role: mergePreferContent(apiResume.target_role, localResume.target_role, ''),
            highlight_color: mergePreferContent(apiResume.highlight_color, localResume.highlight_color, '#3b82f6'),
            experience: mergePreferContent(apiResume.experience, localResume.experience, []),
            education: mergePreferContent(apiResume.education, localResume.education, []),
            training: mergePreferContent(apiResume.training, localResume.training, []),
            core_skills: mergePreferContent(apiResume.core_skills, localResume.core_skills, []),
            skills: mergePreferContent(apiResume.skills, localResume.skills, []),
            projects: mergePreferContent(apiResume.projects, localResume.projects, []),
            certifications: mergePreferContent(apiResume.certifications, localResume.certifications, []),
            languages: mergePreferContent(apiResume.languages, localResume.languages, []),
            portfolio_files: mergePreferContent(apiResume.portfolio_files, localResume.portfolio_files, []),
            google_site_url: mergePreferContent(apiResume.google_site_url, localResume.google_site_url, '')
          };
        }
        return { ...apiResume, owner_user_id: apiResume.owner_user_id || userId };
      });
      
      // Add any user-scoped local resumes that aren't in API
      localResumes.forEach(localResume => {
        const exists = mergedResumes.some(mr => 
          (mr.id === localResume.id || mr._id === localResume._id || mr.id === localResume._id)
        );
        if (!exists) {
          mergedResumes.push(localResume);
        }
      });

      // Apply a minimal set of filters consistently (even when API succeeds).
      let filtered = mergedResumes;
      if (filters.is_primary !== undefined) {
        const want = filters.is_primary === 'true' || filters.is_primary === true;
        filtered = filtered.filter(r => r?.is_primary === want);
      }
      if (filters.public_link_id) {
        filtered = filtered.filter(r => r?.public_link_id === filters.public_link_id);
      }

      // CRITICAL: Deduplicate before tombstone filter (prevents creating MORE resumes)
      console.log('[fetchAll] BEFORE deduplication:', filtered.length, 'resumes');
      const seenIds = new Set();
      const deduplicated = [];
      for (const r of filtered) {
        const uniqueKey = String(r?.id || r?._id || '');
        if (!uniqueKey) continue; // Skip resumes without IDs
        if (seenIds.has(uniqueKey)) {
          console.warn('[fetchAll] ⚠️ DUPLICATE DETECTED! Skipping resume with id:', uniqueKey);
          continue;
        }
        seenIds.add(uniqueKey);
        deduplicated.push(r);
      }
      console.log('[fetchAll] AFTER deduplication:', deduplicated.length, 'resumes');
      filtered = deduplicated;

      // Hide tombstoned (client-deleted) resumes for this user.
      // REMOVED SAFETY: User explicitly deleted these, so they should stay gone!
      console.log('[fetchAll] Filtering tombstones for userId:', userId);
      const beforeTombstoneCount = filtered.length;
      console.log('[fetchAll] Resumes BEFORE tombstone filter:', filtered.map(r => ({ id: r?.id || r?._id, public_link_id: r?.public_link_id })));
      const withoutTombstones = filtered.filter(r => !isResumeTombstonedForUser(userId, r));
      console.log('[fetchAll] Before tombstone:', beforeTombstoneCount, 'After tombstone:', withoutTombstones.length);
      console.log('[fetchAll] Resumes AFTER tombstone filter:', withoutTombstones.map(r => ({ id: r?.id || r?._id, public_link_id: r?.public_link_id })));
      // Trust the user's delete actions - no safety override!
      filtered = withoutTombstones;

      return filtered;
    } catch (error) {
      // Network error or other issues - fall back to localStorage
      if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
        console.warn('Network error fetching resumes, using localStorage fallback');
        const localResumes = getLocalResumesForUser(userId);
        let filtered = localResumes;
        if (filters.is_primary !== undefined) {
          filtered = filtered.filter(r => r.is_primary === (filters.is_primary === 'true' || filters.is_primary === true));
        }
        if (filters.public_link_id) {
          filtered = filtered.filter(r => r.public_link_id === filters.public_link_id);
        }
        // CRITICAL: Filter tombstones in network error fallback path!
        console.log('[Fetch Fallback Network] Filtering tombstones...');
        filtered = filtered.filter(r => !isResumeTombstonedForUser(userId, r));
        return filtered;
      }
      console.error('Error fetching resumes:', error);
      throw error;
    }
  },

  /**
   * Fetch a single resume entity by ID
   */
  async fetchById(entityId) {
    const userId = getCurrentUserId();
    const user = getCurrentUserSession();
    const email = user?.email || null;
    if (!userId) throw new Error(`Resume not found: ${entityId}`);
    // Tombstones are a client-side UI convenience; never hard-block reading a resume by id.
    const localResume = getLocalResumeByIdForUser(userId, entityId);
    
    try {
      const response = await fetch(`${API_BASE_URL}/me/resumes/${entityId}`, {
        headers: getHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || '';
        
        // If Resume entity doesn't exist or has issues, use localStorage
        if (errorMessage.includes('Entity schema') || 
            errorMessage.includes('not found in app') ||
            response.status === 404) {
          if (localResume) {
            console.warn('Resume entity not found in Base44, using localStorage');
            return localResume;
          }
          throw new Error(`Resume not found: ${entityId}`);
        }
        
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      let apiResume = null;
      if (data && data.resume) {
        // AWS backend returns { resume: {...} }
        apiResume = data.resume;
      } else if (data && data.data) {
        apiResume = data.data;
      } else if (data && data.result) {
        apiResume = data.result;
      } else {
        apiResume = data;
      }

      // Ownership check (strict)
      const owned =
        apiResume?.owner_user_id === userId ||
        apiResume?.user_id === userId ||
        (!!email && resumeMatchesEmail(apiResume, email));
      if (!owned) {
        throw new Error(`Resume not found: ${entityId}`);
      }
      
      // Merge with localStorage to preserve fields (including training)
      if (localResume) {
        return {
          ...apiResume,
          owner_user_id: localResume.owner_user_id || apiResume.owner_user_id || userId,
          pronouns: mergePreferContent(apiResume.pronouns, localResume.pronouns, ''),
          credentials: mergePreferContent(apiResume.credentials, localResume.credentials, ''),
          professional_summary: mergePreferContent(apiResume.professional_summary, localResume.professional_summary, ''),
          profile_frame: mergePreferContent(apiResume.profile_frame, localResume.profile_frame, ''),
          experience: mergePreferContent(apiResume.experience, localResume.experience, []),
          education: mergePreferContent(apiResume.education, localResume.education, []),
          training: mergePreferContent(apiResume.training, localResume.training, []),
          core_skills: mergePreferContent(apiResume.core_skills, localResume.core_skills, []),
          skills: mergePreferContent(apiResume.skills, localResume.skills, []),
          projects: mergePreferContent(apiResume.projects, localResume.projects, []),
          certifications: mergePreferContent(apiResume.certifications, localResume.certifications, []),
          languages: mergePreferContent(apiResume.languages, localResume.languages, []),
          portfolio_files: mergePreferContent(apiResume.portfolio_files, localResume.portfolio_files, []),
          google_site_url: mergePreferContent(apiResume.google_site_url, localResume.google_site_url, '')
        };
      }
      
      return apiResume;
    } catch (error) {
      if ((error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) && localResume) {
        console.warn('Network error fetching resume, using localStorage');
        return localResume;
      }
      if (localResume) {
        console.warn('API fetch failed, using localStorage version');
        return localResume;
      }
      console.error('Error fetching resume:', error);
      throw error;
    }
  },

  /**
   * Fetch a resume entity by ID WITHOUT ownership checks.
   * SELECT-only. Used only for read-only profile viewing when `uid/public_link_id` are missing/unstable.
   */
  async fetchByIdUnscoped(entityId) {
    const id = (entityId || '').trim();
    if (!id) return null;
    try {
      const response = await fetch(`${API_BASE_URL}/me/resumes/${id}`, {
        headers: getHeaders()
      });
      if (!response.ok) return null;
      const data = await response.json();
      if (data && data.data) return data.data;
      if (data && data.result) return data.result;
      return data || null;
    } catch {
      return null;
    }
  },

  /**
   * SELECT-only fallback: fetch all and find by entity id.
   * Useful when /me/resumes/:id is not accessible for non-owners.
   */
  async fetchByIdViaList(entityId) {
    const id = (entityId || '').trim();
    if (!id) return null;
    try {
      const response = await fetch(`${API_BASE_URL}/me/resumes`, { headers: getHeaders() });
      if (!response.ok) return null;
      const data = await response.json();
      const list = Array.isArray(data)
        ? data
        : (data?.data && Array.isArray(data.data) ? data.data
          : (data?.results && Array.isArray(data.results) ? data.results
            : (data?.items && Array.isArray(data.items) ? data.items : (data ? [data] : []))));
      const match = (Array.isArray(list) ? list : []).find(r => String(r?.id || r?._id || '') === String(id));
      return match || null;
    } catch {
      return null;
    }
  },

  /**
   * SELECT-only diagnostics: fetch all and find by entity id (with HTTP status).
   */
  async fetchByIdViaListWithMeta(entityId) {
    const id = (entityId || '').trim();
    if (!id) return { ok: false, status: 0, item: null };
    try {
      const response = await fetch(`${API_BASE_URL}/me/resumes`, { headers: getHeaders() });
      const status = response.status;
      if (!response.ok) return { ok: false, status, item: null };
      const data = await response.json();
      const list = Array.isArray(data)
        ? data
        : (data?.data && Array.isArray(data.data) ? data.data
          : (data?.results && Array.isArray(data.results) ? data.results
            : (data?.items && Array.isArray(data.items) ? data.items : (data ? [data] : []))));
      const match = (Array.isArray(list) ? list : []).find(r => String(r?.id || r?._id || '') === String(id));
      return { ok: true, status, item: match || null };
    } catch (e) {
      return { ok: false, status: 0, item: null, error: e?.message || 'network_error' };
    }
  },

  /**
   * Create a new resume entity
   */
  async create(resumeData) {
    const userId = getCurrentUserId();
    if (!userId) throw new Error('Unauthorized');
    // Ensure every profile has a shareable public link.
    const normalized = {
      ...resumeData,
      owner_user_id: resumeData.owner_user_id || userId,
      public_link_id: resumeData.public_link_id || generatePublicLinkId()
    };
    const localResume = createLocalResumeForUser(userId, normalized);

    // Preferred: AWS backend (Cognito + API)
    if (hasAwsBackend()) {
      try {
        const profilePid = normalized?.is_primary ? await fetchAwsProfilePublicLinkId() : null;
        const effectivePid = profilePid || normalized?.public_link_id || null;
        const out = await awsFetchJson('/me/resumes', {
          method: 'POST',
          auth: true,
          body: {
            resume_id: localResume?.id || localResume?._id,
            title: normalized?.title || '',
            template: normalized?.template || '',
            is_primary: !!normalized?.is_primary,
            public_link_id: effectivePid,
            import_source: normalized?.import_source || null,
            imported_at: normalized?.imported_at || null,
            resume: { ...normalized, public_link_id: effectivePid }
          }
        });
        const it = out?.resume || out || {};
        const rid = it?.resume_id || it?.id || it?._id || localResume?.id || localResume?._id;
        const resumeObj = (it?.resume && typeof it.resume === 'object') ? it.resume : null;
        const flat = { ...(resumeObj || {}), ...(it || {}) };
        delete flat.resume;
        delete flat.resume_id;
        if (rid) {
          flat.id = rid;
          flat._id = rid;
        }
        flat.owner_user_id = flat.owner_user_id || userId;
        flat.created_at = flat.created_at || flat.createdAt || flat.created;
        flat.updated_at = flat.updated_at || flat.updatedAt || flat.updated;

        try {
          updateLocalResumeForUser(userId, localResume.id || localResume._id, { ...flat, owner_user_id: userId });
        } catch {
          // ignore
        }
        return flat;
      } catch (e) {
        console.warn('AWS create failed; saved locally only', e);
        return { ...localResume, __save_status: 'local_only', __save_error: e?.message || 'network_error' };
      }
    }
    
    try {
      // Try to create via API
      const response = await fetch(`${API_BASE_URL}/me/resumes`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(normalized)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || '';
        
        if (errorMessage.includes('Entity schema') || 
            errorMessage.includes('not found in app') ||
            response.status === 404) {
          console.warn('Resume entity not found in Base44, using localStorage only');
          return localResume;
        }
        console.warn('API create failed, but saved to localStorage:', errorMessage);
        return localResume;
      }

      const data = await response.json();
      // Handle different response formats
      let createdResume = null;
      if (data && data.data) {
        createdResume = data.data;
      } else if (data && data.result) {
        createdResume = data.result;
      } else {
        createdResume = data;
      }
      
      // Update localStorage with API ID if different
      if (createdResume.id || createdResume._id) {
        const apiId = createdResume.id || createdResume._id;
        updateLocalResumeForUser(userId, localResume.id || localResume._id, {
          ...createdResume,
          id: apiId,
          _id: apiId,
          owner_user_id: localResume.owner_user_id || userId,
          pronouns: mergePreferContent(createdResume.pronouns, localResume.pronouns, ''),
          credentials: mergePreferContent(createdResume.credentials, localResume.credentials, ''),
          professional_summary: mergePreferContent(createdResume.professional_summary, localResume.professional_summary, ''),
          profile_frame: mergePreferContent(createdResume.profile_frame, localResume.profile_frame, ''),
          experience: mergePreferContent(createdResume.experience, localResume.experience, []),
          education: mergePreferContent(createdResume.education, localResume.education, []),
          training: mergePreferContent(createdResume.training, localResume.training, []),
          core_skills: mergePreferContent(createdResume.core_skills, localResume.core_skills, []),
          skills: mergePreferContent(createdResume.skills, localResume.skills, []),
          projects: mergePreferContent(createdResume.projects, localResume.projects, []),
          certifications: mergePreferContent(createdResume.certifications, localResume.certifications, []),
          languages: mergePreferContent(createdResume.languages, localResume.languages, []),
          portfolio_files: mergePreferContent(createdResume.portfolio_files, localResume.portfolio_files, []),
          google_site_url: mergePreferContent(createdResume.google_site_url, localResume.google_site_url, '')
        });
        return {
          ...createdResume,
          owner_user_id: localResume.owner_user_id || userId,
          pronouns: mergePreferContent(createdResume.pronouns, localResume.pronouns, ''),
          credentials: mergePreferContent(createdResume.credentials, localResume.credentials, ''),
          professional_summary: mergePreferContent(createdResume.professional_summary, localResume.professional_summary, ''),
          profile_frame: mergePreferContent(createdResume.profile_frame, localResume.profile_frame, ''),
          experience: mergePreferContent(createdResume.experience, localResume.experience, []),
          education: mergePreferContent(createdResume.education, localResume.education, []),
          training: mergePreferContent(createdResume.training, localResume.training, []),
          core_skills: mergePreferContent(createdResume.core_skills, localResume.core_skills, []),
          skills: mergePreferContent(createdResume.skills, localResume.skills, []),
          projects: mergePreferContent(createdResume.projects, localResume.projects, []),
          certifications: mergePreferContent(createdResume.certifications, localResume.certifications, []),
          languages: mergePreferContent(createdResume.languages, localResume.languages, []),
          portfolio_files: mergePreferContent(createdResume.portfolio_files, localResume.portfolio_files, []),
          google_site_url: mergePreferContent(createdResume.google_site_url, localResume.google_site_url, '')
        };
      }
      
      return {
        ...createdResume,
        owner_user_id: localResume.owner_user_id || userId,
        pronouns: mergePreferContent(createdResume.pronouns, localResume.pronouns, ''),
        credentials: mergePreferContent(createdResume.credentials, localResume.credentials, ''),
        professional_summary: mergePreferContent(createdResume.professional_summary, localResume.professional_summary, ''),
        profile_frame: mergePreferContent(createdResume.profile_frame, localResume.profile_frame, ''),
        experience: mergePreferContent(createdResume.experience, localResume.experience, []),
        education: mergePreferContent(createdResume.education, localResume.education, []),
        training: mergePreferContent(createdResume.training, localResume.training, []),
        core_skills: mergePreferContent(createdResume.core_skills, localResume.core_skills, []),
        skills: mergePreferContent(createdResume.skills, localResume.skills, []),
        projects: mergePreferContent(createdResume.projects, localResume.projects, []),
        certifications: mergePreferContent(createdResume.certifications, localResume.certifications, []),
        languages: mergePreferContent(createdResume.languages, localResume.languages, []),
        portfolio_files: mergePreferContent(createdResume.portfolio_files, localResume.portfolio_files, []),
        google_site_url: mergePreferContent(createdResume.google_site_url, localResume.google_site_url, '')
      };
    } catch (error) {
      // Network error or other issues - return localStorage version
      if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
        console.warn('Network error creating resume, using localStorage only');
        return localResume;
      }
      console.error('Error creating resume:', error);
      // Still return localStorage version as fallback
      return localResume;
    }
  },

  /**
   * SAFETY-FIRST SYNC (local -> Base44)
   * - Scopes to CURRENT user only.
   * - Never deletes anything.
   * - For existing server resumes, only fills fields that are missing on the server
   *   (does NOT overwrite non-empty server fields).
   * - For local resumes not found on the server, creates new server records.
   */
  async syncCurrentUserResumesToBase44() {
    // Function name kept for backward compatibility, but now calls AWS
    const user = getCurrentUserSession();
    const userId = getCurrentUserId();
    const email = user?.email || null;
    if (!userId) throw new Error('Unauthorized');
    if (!hasAuthToken()) throw new Error('Not authenticated. Please log in.');

    const parseList = async (response) => {
      const data = await response.json();
      if (Array.isArray(data)) return data;
      if (data?.data && Array.isArray(data.data)) return data.data;
      if (data?.results && Array.isArray(data.results)) return data.results;
      if (data?.items && Array.isArray(data.items)) return data.items;
      return data ? [data] : [];
    };

    const tryFetch = async (filters) => {
      const query = filters ? new URLSearchParams(filters).toString() : '';
      const url = `${API_BASE_URL}/me/resumes${query ? `?${query}` : ''}`;
      const res = await fetch(url, { headers: getHeaders() });
      if (!res.ok) return [];
      return await parseList(res);
    };

    const isOwnedByUser = (r) => {
      if (!r) return false;
      if (r?.owner_user_id) return String(r.owner_user_id) === String(userId);
      if (r?.user_id) return String(r.user_id) === String(userId);
      return !!email && resumeMatchesEmail(r, email);
    };

    // Fetch owned server resumes (best-effort, but scoped)
    let api = await tryFetch({ owner_user_id: userId });
    if (api.length === 0) api = await tryFetch({ user_id: userId });
    if (api.length === 0 && email) {
      const e = String(email).trim();
      api = [
        ...await tryFetch({ contact_email: e }),
        ...await tryFetch({ contactEmail: e }),
        ...await tryFetch({ user_email: e }),
        ...await tryFetch({ userEmail: e }),
        ...await tryFetch({ email: e }),
        ...await tryFetch({ owner_email: e }),
        ...await tryFetch({ ownerEmail: e })
      ];
    }
    if (api.length === 0) {
      api = await tryFetch(null);
    }
    const apiOwned = (Array.isArray(api) ? api : []).filter(isOwnedByUser);

    const apiById = new Map();
    const apiByPublic = new Map();
    for (const r of apiOwned) {
      const rid = r?.id || r?._id;
      const pid = r?.public_link_id;
      if (rid) apiById.set(String(rid), r);
      if (pid) apiByPublic.set(String(pid), r);
    }

    const local = (getLocalResumesForUser(userId) || []).filter(r => !isResumeTombstonedForUser(userId, r));

    const willFillAnyField = (existing, incoming) => {
      if (!existing || !incoming) return false;
      for (const [k, v] of Object.entries(incoming)) {
        if (k === 'owner_user_id') continue;
        if (Array.isArray(v)) {
          if (isEmptyArray(existing[k]) && !isEmptyArray(v)) return true;
        } else if (typeof v === 'object' && v !== null) {
          if (existing[k] == null) return true;
        } else {
          if (isEmpty(existing[k]) && !isEmpty(v)) return true;
        }
      }
      return false;
    };

    const stripIds = (obj) => {
      const o = { ...(obj || {}) };
      delete o.id;
      delete o._id;
      return o;
    };

    const computeDroppedKeys = (sent, received) => {
      const s = sent || {};
      const r = received || {};
      const dropped = [];
      for (const [k, v] of Object.entries(s)) {
        if (k === 'owner_user_id') continue;
        // ignore pure client markers / transient fields
        if (k === 'base44_synced_at') continue;
        // Only consider fields where we actually sent meaningful data
        const meaningful =
          Array.isArray(v) ? !isEmptyArray(v)
            : (typeof v === 'object' && v !== null) ? true
              : !isEmpty(v);
        if (!meaningful) continue;

        // If server did not echo the field back (or echoed as empty), consider it dropped.
        const rv = r?.[k];
        const presentAndMeaningful =
          Array.isArray(rv) ? !isEmptyArray(rv)
            : (typeof rv === 'object' && rv !== null) ? true
              : !isEmpty(rv);
        if (!presentAndMeaningful) dropped.push(k);
      }
      return dropped;
    };

    const encodeJson = (v) => {
      try {
        if (v === undefined) return '';
        return JSON.stringify(v ?? null);
      } catch {
        return '';
      }
    };

    const buildSchemaCompatPayload = (resume) => {
      const exp = Array.isArray(resume?.experience)
        ? resume.experience
        : (Array.isArray(resume?.work_experience) ? resume.work_experience : (Array.isArray(resume?.workExperience) ? resume.workExperience : []));
      const edu = Array.isArray(resume?.education) ? resume.education : (Array.isArray(resume?.educations) ? resume.educations : []);
      const training = Array.isArray(resume?.training) ? resume.training : (Array.isArray(resume?.professional_development) ? resume.professional_development : []);
      const certs = Array.isArray(resume?.certifications)
        ? resume.certifications
        : (Array.isArray(resume?.licenses) ? resume.licenses : (Array.isArray(resume?.license_or_certification) ? resume.license_or_certification : []));
      const langs = Array.isArray(resume?.languages) ? resume.languages : (Array.isArray(resume?.language) ? resume.language : []);
      const projects = Array.isArray(resume?.projects) ? resume.projects : [];
      const skills = Array.isArray(resume?.skills) ? resume.skills : [];
      const coreSkills = Array.isArray(resume?.core_skills) ? resume.core_skills : [];

      // These *_json fields require you to add matching fields to Base44's Resume entity schema.
      // Once added, Base44 will store them, and our UI will rehydrate them in normalizeResumeForFullView().
      return {
        experience_json: encodeJson(exp),
        education_json: encodeJson(edu),
        training_json: encodeJson(training),
        certifications_json: encodeJson(certs),
        languages_json: encodeJson(langs),
        projects_json: encodeJson(projects),
        skills_json: encodeJson(skills),
        core_skills_json: encodeJson(coreSkills),
        // Full backup blob (optional field in schema)
        profile_json: encodeJson({
          experience: exp,
          education: edu,
          training,
          certifications: certs,
          languages: langs,
          projects,
          skills,
          core_skills: coreSkills
        })
      };
    };

    let created = 0;
    let updated = 0;
    let skipped = 0;
    const errors = [];
    const details = [];

    for (const lr of local) {
      const localRid = lr?.id || lr?._id || null;
      const localPid = lr?.public_link_id || null;
      const match =
        (localRid && apiById.get(String(localRid))) ||
        (localPid && apiByPublic.get(String(localPid))) ||
        null;

      if (match) {
        const serverId = match?.id || match?._id || null;
        if (!serverId) {
          skipped++;
          details.push({ action: 'skip', title: lr?.title || '', serverId: null, droppedKeys: [], reason: 'missing_server_id' });
          continue;
        }

        // Only fill server-missing fields using local data (non-destructive)
        const merged = safeMergeResume(
          match,
          {
            ...lr,
            // Keep/ensure identifiers
            public_link_id: lr?.public_link_id || match?.public_link_id || generatePublicLinkId(),
            owner_user_id: match?.owner_user_id || match?.user_id || lr?.owner_user_id || userId,
            ...buildSchemaCompatPayload(lr)
          }
        );
        merged.owner_user_id = match?.owner_user_id || match?.user_id || userId;

        if (!willFillAnyField(match, merged)) {
          skipped++;
          details.push({ action: 'skip', title: lr?.title || '', serverId: String(serverId), droppedKeys: [], reason: 'nothing_to_fill' });
          continue;
        }

        try {
          const res = await fetch(`${API_BASE_URL}/me/resumes/${serverId}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(stripIds(merged))
          });
          if (!res.ok) {
            const msg = (await res.json().catch(() => ({})))?.message || `${res.status} ${res.statusText}`;
            errors.push({ action: 'update', serverId: String(serverId), title: lr?.title || '', message: msg });
            details.push({ action: 'update_failed', title: lr?.title || '', serverId: String(serverId), droppedKeys: [], reason: msg });
            continue;
          }

          let updatedRecord = null;
          try {
            const data = await res.json();
            updatedRecord = data?.data || data?.result || data;
          } catch {
            // some APIs don't return JSON on PUT; treat as unknown
            updatedRecord = null;
          }
          const droppedKeys = updatedRecord ? computeDroppedKeys(stripIds(merged), updatedRecord) : [];
          details.push({ action: 'updated', title: lr?.title || '', serverId: String(serverId), droppedKeys });

          updated++;
          // Mark local as linked to server id for future operations
          try {
            updateLocalResumeForUser(userId, localRid || serverId, {
              _id: serverId,
              public_link_id: merged.public_link_id,
              base44_synced_at: new Date().toISOString()
            });
          } catch {
            // ignore local sync marker failures
          }
        } catch (e) {
          errors.push({ action: 'update', serverId: String(serverId), title: lr?.title || '', message: e?.message || 'network_error' });
          details.push({ action: 'update_failed', title: lr?.title || '', serverId: String(serverId), droppedKeys: [], reason: e?.message || 'network_error' });
        }

        continue;
      }

      // No server match — create a new server record
      const payload = {
        ...lr,
        owner_user_id: lr?.owner_user_id || userId,
        public_link_id: lr?.public_link_id || generatePublicLinkId(),
        ...buildSchemaCompatPayload(lr)
      };
      const payloadNoIds = stripIds(payload);

      try {
        const res = await fetch(`${API_BASE_URL}/me/resumes`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(payloadNoIds)
        });
        if (!res.ok) {
          const msg = (await res.json().catch(() => ({})))?.message || `${res.status} ${res.statusText}`;
          errors.push({ action: 'create', title: lr?.title || '', message: msg });
          details.push({ action: 'create_failed', title: lr?.title || '', serverId: null, droppedKeys: [], reason: msg });
          continue;
        }
        const data = await res.json();
        const createdResume = data?.data || data?.result || data;
        const serverId = createdResume?.id || createdResume?._id || null;
        const droppedKeys = createdResume ? computeDroppedKeys(payloadNoIds, createdResume) : [];
        details.push({ action: 'created', title: lr?.title || '', serverId: serverId ? String(serverId) : null, droppedKeys });

        created++;
        if (serverId) {
          apiById.set(String(serverId), createdResume);
          apiByPublic.set(String(payload.public_link_id), createdResume);
          try {
            updateLocalResumeForUser(userId, localRid || lr?.id || lr?._id, {
              _id: serverId,
              public_link_id: payload.public_link_id,
              base44_synced_at: new Date().toISOString()
            });
          } catch {
            // ignore
          }
        }
      } catch (e) {
        errors.push({ action: 'create', title: lr?.title || '', message: e?.message || 'network_error' });
        details.push({ action: 'create_failed', title: lr?.title || '', serverId: null, droppedKeys: [], reason: e?.message || 'network_error' });
      }
    }

    return { created, updated, skipped, totalLocal: local.length, errors, details };
  },

  /**
   * Sync local (user-scoped) resumes to AWS backend.
   * - Scopes to current user only
   * - Never deletes server data
   * - Uses stable resume_id (local id) so records are consistent across devices
   */
  async syncCurrentUserResumesToAWS() {
    const userId = getCurrentUserId();
    if (!userId) throw new Error('Unauthorized');
    if (!hasAwsBackend()) throw new Error('AWS API is not configured. Set VITE_API_BASE_URL and rebuild.');

    const local = (getLocalResumesForUser(userId) || []).filter(r => !isResumeTombstonedForUser(userId, r));
    const errors = [];

    let created = 0;
    let updated = 0;
    let skipped = 0;

    let remote = [];
    try {
      const data = await awsFetchJson('/me/resumes', { auth: true });
      remote = Array.isArray(data?.resumes) ? data.resumes : [];
    } catch (e) {
      throw new Error(e?.message || 'Failed to list remote resumes');
    }

    const remoteIds = new Set(
      (remote || [])
        .map(r => r?.resume_id || r?.id || r?._id)
        .filter(Boolean)
        .map(String)
    );

    // Prefer a single public profile link (stable per user)
    const profilePid = await fetchAwsProfilePublicLinkId();

    for (const lr of local) {
      const rid = String(lr?.id || lr?._id || '');
      if (!rid) { skipped += 1; continue; }

      const normalized = { ...lr, owner_user_id: lr?.owner_user_id || userId };
      const effectivePid = (normalized?.is_primary ? (profilePid || normalized?.public_link_id) : normalized?.public_link_id) || null;
      const payload = {
        resume_id: rid,
        title: normalized?.title || '',
        template: normalized?.template || '',
        is_primary: !!normalized?.is_primary,
        public_link_id: effectivePid,
        resume: { ...normalized, public_link_id: effectivePid }
      };

      try {
        if (remoteIds.has(rid)) {
          await awsFetchJson(`/me/resumes/${encodeURIComponent(rid)}`, { method: 'PUT', auth: true, body: payload });
          updated += 1;
        } else {
          await awsFetchJson('/me/resumes', { method: 'POST', auth: true, body: payload });
          created += 1;
          remoteIds.add(rid);
        }
      } catch (e) {
        errors.push({ id: rid, title: lr?.title || '', message: e?.message || 'network_error' });
      }
    }

    return { created, updated, skipped, totalLocal: local.length, errors };
  },

  /**
   * Update an existing resume entity
   */
  async update(entityId, updateData, { skipProfileSync = false } = {}) {
    const userId = getCurrentUserId();
    if (!userId) throw new Error('Unauthorized');
    const localResume = getLocalResumeByIdForUser(userId, entityId);
    const withPublic = {
      ...updateData,
      // Ensure every profile has a shareable public link (stable once created).
      public_link_id: updateData?.public_link_id || localResume?.public_link_id || generatePublicLinkId()
    };
    if (localResume) {
      // owner_user_id is immutable once set
      updateLocalResumeForUser(userId, entityId, { ...withPublic, owner_user_id: localResume.owner_user_id || userId });
    } else {
      // Only allow creating a local shadow record for current user
      createLocalResumeForUser(userId, { ...withPublic, id: entityId, _id: entityId, owner_user_id: userId });
    }
    
    try {
      const normalized = { ...withPublic, owner_user_id: localResume?.owner_user_id || userId };

      // Preferred: AWS backend (Cognito + API)
      if (hasAwsBackend()) {
        try {
          const profilePid = normalized?.is_primary ? await fetchAwsProfilePublicLinkId() : null;
          const effectivePid = profilePid || normalized?.public_link_id || null;
          
          // If this is a primary resume, also save profile fields to Users table.
          // skipProfileSync=true is used by portfolio operations so they NEVER
          // touch the Users table (experience, education, skills, etc.).
          if (normalized?.is_primary && !skipProfileSync) {
            try {
              await awsFetchJson('/me/profile', {
                method: 'PUT',
                auth: true,
                body: {
                  name: normalized?.title || '',
                  headline: normalized?.headline || '',
                  about: normalized?.summary || normalized?.about || '',
                  professionalSummary: normalized?.professional_summary || normalized?.professionalSummary || '',
                  location: normalized?.location || '',
                  targetRole: normalized?.target_role || normalized?.targetRole || '',
                  credentials: normalized?.credentials || '',
                  pronouns: normalized?.pronouns || '',
                  highlight_color: normalized?.highlight_color || normalized?.highlightColor || '',
                  profile_frame: normalized?.profile_frame || normalized?.profileFrame || '',
                  linkedin_url: normalized?.linkedin_url || normalized?.linkedinUrl || '',
                  website_url: normalized?.website_url || normalized?.websiteUrl || '',
                  photo_url: normalized?.photo_url || normalized?.photoUrl || '',
                  banner_url: normalized?.banner_url || normalized?.bannerUrl || '',
                  // Save rich content fields to Users table
                  experience: normalized?.experience || [],
                  education: normalized?.education || [],
                  skills: normalized?.skills || [],
                  projects: normalized?.projects || [],
                  certifications: normalized?.certifications || [],
                  languages: normalized?.languages || [],
                  training: normalized?.training || [],
                  credly_badges: normalized?.credly_badges || normalized?.credlyBadges || [],
                  core_skills: normalized?.core_skills || normalized?.coreSkills || []
                }
              });
            } catch (profileErr) {
              console.warn('Failed to update Users table profile (resume update will continue):', profileErr);
            }
          }
          
          const putBody = {
              resume_id: entityId,
              public_link_id: effectivePid,
              resume: { ...normalized, public_link_id: effectivePid }
          };
          if (Object.prototype.hasOwnProperty.call(updateData, 'title'))      putBody.title = normalized?.title || '';
          if (Object.prototype.hasOwnProperty.call(updateData, 'template'))   putBody.template = normalized?.template || '';
          if (Object.prototype.hasOwnProperty.call(updateData, 'is_primary')) putBody.is_primary = !!normalized?.is_primary;

          const out = await awsFetchJson(`/me/resumes/${encodeURIComponent(String(entityId))}`, {
            method: 'PUT',
            auth: true,
            body: putBody
          });
          const it = out?.resume || out || {};
          const rid = it?.resume_id || it?.id || it?._id || entityId;
          const resumeObj = (it?.resume && typeof it.resume === 'object') ? it.resume : null;
          const flat = { ...(resumeObj || {}), ...(it || {}) };
          delete flat.resume;
          delete flat.resume_id;
          if (rid) {
            flat.id = rid;
            flat._id = rid;
          }
          flat.owner_user_id = flat.owner_user_id || userId;
          flat.created_at = flat.created_at || flat.createdAt || flat.created;
          flat.updated_at = flat.updated_at || flat.updatedAt || flat.updated;

          try {
            updateLocalResumeForUser(userId, entityId, { ...flat, owner_user_id: userId });
          } catch {
            // ignore
          }
          return flat;
        } catch (e) {
          console.warn('AWS update failed; using local-only', e);
          return {
            ...(getLocalResumeByIdForUser(userId, entityId) || updateData),
            __save_status: 'local_only',
            __save_http_status: e?.status || 0,
            __save_error: e?.message || 'network_error'
          };
        }
      }

      const response = await fetch(`${API_BASE_URL}/me/resumes/${entityId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(normalized)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || '';

        const isNotFound =
          response.status === 404 ||
          /not found/i.test(errorMessage || '') ||
          /Entity Resume with ID/i.test(errorMessage || '');

        // Self-heal: local record id may not match server id.
        // If the server says this id doesn't exist, try to locate the correct server record
        // (by public_link_id / ownership) and update that instead, or create a new server record.
        if (isNotFound) {
          try {
            const email = getCurrentUserSession()?.email || null;
            const pid = normalized?.public_link_id || null;

            const parseList = async (res) => {
              const data = await res.json();
              if (Array.isArray(data)) return data;
              if (data && Array.isArray(data.data)) return data.data;
              if (data && Array.isArray(data.results)) return data.results;
              if (data && Array.isArray(data.items)) return data.items;
              return data ? [data] : [];
            };

            const isOwnedByUser = (r) => {
              if (!r) return false;
              if (r.owner_user_id) return String(r.owner_user_id) === String(userId);
              if (r.user_id) return String(r.user_id) === String(userId);
              return !!email && resumeMatchesEmail(r, email);
            };

            const tryUpdateServerId = async (serverId) => {
              if (!serverId) return null;
              const res2 = await fetch(`${API_BASE_URL}/me/resumes/${serverId}`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify(normalized)
              });
              if (!res2.ok) return null;
              const data2 = await res2.json();
              return data2?.data || data2?.result || data2;
            };

            // A) If we have a public_link_id, resolve the published server record by it.
            let repaired = null;
            let repairedId = null;
            if (pid) {
              const byPublic = await fetch(`${API_BASE_URL}/me/resumes?${new URLSearchParams({ public_link_id: pid }).toString()}`, { headers: getHeaders() });
              if (byPublic.ok) {
                const list = await parseList(byPublic);
                const owned = (Array.isArray(list) ? list : []).find(isOwnedByUser) || null;
                repairedId = owned?.id || owned?._id || null;
                if (repairedId && String(repairedId) !== String(entityId)) {
                  repaired = await tryUpdateServerId(repairedId);
                }
              }
            }

            // B) If still not repaired, try to find any owned server record and update it.
            if (!repaired) {
              const allRes = await fetch(`${API_BASE_URL}/me/resumes`, { headers: getHeaders() });
              if (allRes.ok) {
                const all = await parseList(allRes);
                const ownedAny = (Array.isArray(all) ? all : []).find(isOwnedByUser) || null;
                const sid = ownedAny?.id || ownedAny?._id || null;
                if (sid && String(sid) !== String(entityId)) {
                  repairedId = sid;
                  repaired = await tryUpdateServerId(sid);
                }
              }
            }

            // C) If no server record exists, create one with this data.
            if (!repaired) {
              const createRes = await fetch(`${API_BASE_URL}/me/resumes`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({
                  ...normalized,
                  owner_user_id: userId
                })
              });
              if (createRes.ok) {
                const createdData = await createRes.json();
                repaired = createdData?.data || createdData?.result || createdData;
                repairedId = repaired?.id || repaired?._id || null;
              }
            }

            // If we repaired/created on server, ensure local store has a record keyed by the server id
            // so future saves don't keep hitting 404. We do NOT delete the old local record.
            if (repairedId) {
              // Demote old local primary if different id.
              if (entityId && String(entityId) !== String(repairedId)) {
                try {
                  const existingOld = getLocalResumeByIdForUser(userId, entityId);
                  if (existingOld && existingOld.is_primary) {
                    updateLocalResumeForUser(userId, entityId, { ...existingOld, is_primary: false });
                  }
                } catch {
                  // ignore
                }
              }
              // Upsert local by repairedId
              try {
                const existingNew = getLocalResumeByIdForUser(userId, repairedId);
                if (existingNew) {
                  updateLocalResumeForUser(userId, repairedId, {
                    ...existingNew,
                    ...normalized,
                    id: repairedId,
                    _id: repairedId,
                    owner_user_id: userId,
                    is_primary: true
                  });
                } else {
                  createLocalResumeForUser(userId, {
                    ...normalized,
                    id: repairedId,
                    _id: repairedId,
                    owner_user_id: userId,
                    is_primary: true
                  });
                }
              } catch {
                // ignore
              }
            }

            if (repaired) {
              return repaired;
            }
          } catch (repairErr) {
            console.warn('Resume update repair failed; falling back to local-only', repairErr);
          }
        }

        if (errorMessage.includes('Entity schema') ||
            errorMessage.includes('not found in app') ||
            response.status === 404) {
          console.warn('Resume entity not found in Base44, using localStorage only');
          return {
            ...(getLocalResumeByIdForUser(userId, entityId) || updateData),
            __save_status: 'local_only',
            __save_http_status: response.status,
            __save_error: errorMessage || response.statusText
          };
        }
        console.warn('API update failed, but saved to localStorage:', errorMessage);
        return {
          ...(getLocalResumeByIdForUser(userId, entityId) || updateData),
          __save_status: 'local_only',
          __save_http_status: response.status,
          __save_error: errorMessage || response.statusText
        };
      }

      const data = await response.json();
      let updatedResume = null;
      if (data && data.data) {
        updatedResume = data.data;
      } else if (data && data.result) {
        updatedResume = data.result;
      } else {
        updatedResume = data;
      }
      
      const localResumeAfter = getLocalResumeByIdForUser(userId, entityId);
      if (localResumeAfter) {
        return {
          ...updatedResume,
          owner_user_id: localResumeAfter.owner_user_id || userId,
          pronouns: mergePreferContent(updatedResume.pronouns, localResumeAfter.pronouns, ''),
          credentials: mergePreferContent(updatedResume.credentials, localResumeAfter.credentials, ''),
          professional_summary: mergePreferContent(updatedResume.professional_summary, localResumeAfter.professional_summary, ''),
          profile_frame: mergePreferContent(updatedResume.profile_frame, localResumeAfter.profile_frame, ''),
          experience: mergePreferContent(updatedResume.experience, localResumeAfter.experience, []),
          education: mergePreferContent(updatedResume.education, localResumeAfter.education, []),
          training: mergePreferContent(updatedResume.training, localResumeAfter.training, []),
          core_skills: mergePreferContent(updatedResume.core_skills, localResumeAfter.core_skills, []),
          skills: mergePreferContent(updatedResume.skills, localResumeAfter.skills, []),
          projects: mergePreferContent(updatedResume.projects, localResumeAfter.projects, []),
          certifications: mergePreferContent(updatedResume.certifications, localResumeAfter.certifications, []),
          languages: mergePreferContent(updatedResume.languages, localResumeAfter.languages, []),
          portfolio_files: mergePreferContent(updatedResume.portfolio_files, localResumeAfter.portfolio_files, []),
          google_site_url: mergePreferContent(updatedResume.google_site_url, localResumeAfter.google_site_url, '')
        };
      }
      
      return updatedResume;
    } catch (error) {
      // Network error or other issues - return localStorage version
      if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
        console.warn('Network error updating resume, using localStorage only');
        return {
          ...(getLocalResumeByIdForUser(userId, entityId) || updateData),
          __save_status: 'local_only',
          __save_http_status: 0,
          __save_error: 'network_error'
        };
      }
      console.error('Error updating resume:', error);
      // Still return localStorage version as fallback
      return {
        ...(getLocalResumeByIdForUser(userId, entityId) || updateData),
        __save_status: 'local_only',
        __save_http_status: 0,
        __save_error: error?.message || 'unknown_error'
      };
    }
  },

  /**
   * Delete a resume entity
   */
  async delete(entityOrResume) {
    // ULTRA-LENIENT DELETE: If the resume is in the UI, user can delete it. Period.
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('You must be logged in to delete a resume');
    }

    const resumeHint = (entityOrResume && typeof entityOrResume === 'object') ? entityOrResume : null;
    const entityId = resumeHint
      ? (resumeHint.id || resumeHint._id || resumeHint.resume_id || resumeHint.resumeId)
      : entityOrResume;
    
    console.log('🗑️ [Delete] ========================================');
    console.log('🗑️ [Delete] Starting AGGRESSIVE delete for resume:', entityId);
    console.log('🗑️ [Delete] User ID:', userId);
    console.log('🗑️ [Delete] ========================================');

    const candidateIds = new Set();
    if (entityId) candidateIds.add(String(entityId));
    if (resumeHint?.id) candidateIds.add(String(resumeHint.id));
    if (resumeHint?._id) candidateIds.add(String(resumeHint._id));
    if (resumeHint?.resume_id) candidateIds.add(String(resumeHint.resume_id));
    if (resumeHint?.resumeId) candidateIds.add(String(resumeHint.resumeId));

    // Check if resume exists in user-scoped storage
    let local = entityId ? getLocalResumeByIdForUser(userId, entityId) : null;
    console.log('[Delete] User-scoped resume found:', !!local);
    
    // If not in user-scoped storage, check legacy global storage
    if (!local) {
      console.log('[Delete] Checking legacy storage...');
      const legacy = getLegacyLocalResumes();
      local = legacy.find(r => (r.id === entityId || r._id === entityId || r.resume_id === entityId || r.resumeId === entityId));
      console.log('[Delete] Legacy resume found:', !!local);
    }
    
    // If STILL not found, check if it's in the current loaded resumes list
    // (This handles edge cases where resume is in a different storage location)
    if (!local) {
      console.log('[Delete] Attempting to find in loaded resumes list...');
      try {
        const allResumes = await this.fetchAllBestEffortForCurrentUser();
        local = allResumes.find(r => (
          r.id === entityId ||
          r._id === entityId ||
          r.resume_id === entityId ||
          r.resumeId === entityId
        ));
        console.log('[Delete] Found in loaded list:', !!local);
      } catch (err) {
        console.warn('[Delete] Could not fetch resumes list:', err);
      }
    }

    if (local) {
      if (local.id) candidateIds.add(String(local.id));
      if (local._id) candidateIds.add(String(local._id));
      if (local.resume_id) candidateIds.add(String(local.resume_id));
      if (local.resumeId) candidateIds.add(String(local.resumeId));
    }
    
    // CRITICAL: If we can't find the resume anywhere, we still proceed with tombstone + local cleanup
    // This ensures the UI updates even if storage is inconsistent
    const publicLinkId = resumeHint?.public_link_id || local?.public_link_id || null;
    console.log('[Delete] Public Link ID:', publicLinkId);
    console.log('[Delete] Proceeding with delete (found or not)...');

    // COMPLETELY IGNORE SERVER ERRORS - Just delete locally
    try {
      console.log('[Delete] Attempting best-effort server delete...');
      if (hasAwsBackend()) {
        for (const rid of candidateIds) {
          try {
            await awsFetchJson(`/me/resumes/${encodeURIComponent(String(rid))}`, {
              method: 'DELETE',
              auth: true
            });
            console.log('[Delete] AWS delete OK for resume:', rid);
          } catch (awsErr) {
            console.warn('[Delete] AWS delete failed (ignoring):', awsErr?.message || awsErr);
          }
        }
      }
      try {
        if (entityId) {
          const response = await fetch(`${API_BASE_URL}/me/resumes/${entityId}`, {
            method: 'DELETE',
            headers: getHeaders()
          });
          console.log('[Delete] Server response:', response.status, response.ok ? 'OK' : 'Not OK');
        }
      } catch (serverErr) {
        console.warn('[Delete] Server delete failed (ignoring):', serverErr.message);
      }
    } catch (err) {
      console.warn('[Delete] Server communication failed (ignoring):', err);
    }

    // ALWAYS proceed with local delete regardless of server result
    console.log('[Delete] Proceeding with LOCAL delete (regardless of server)...');
    
    // CRITICAL: Tombstone with BOTH id and _id to catch all variations
    console.log('[Delete] Adding comprehensive tombstone...');
    addResumeTombstoneForUser(userId, { id: entityId, public_link_id: publicLinkId });
    // Also tombstone with _id if different
    if (local && local._id && String(local._id) !== String(entityId)) {
      console.log('[Delete] Also tombstoning _id:', local._id);
      addResumeTombstoneForUser(userId, { id: local._id, public_link_id: publicLinkId });
    }
    // Also tombstone with id if we have it from local
    if (local && local.id && String(local.id) !== String(entityId)) {
      console.log('[Delete] Also tombstoning alternate id:', local.id);
      addResumeTombstoneForUser(userId, { id: local.id, public_link_id: publicLinkId });
    }
    if (local && local.resume_id && String(local.resume_id) !== String(entityId)) {
      console.log('[Delete] Also tombstoning resume_id:', local.resume_id);
      addResumeTombstoneForUser(userId, { id: local.resume_id, public_link_id: publicLinkId });
    }
    if (local && local.resumeId && String(local.resumeId) !== String(entityId)) {
      console.log('[Delete] Also tombstoning resumeId:', local.resumeId);
      addResumeTombstoneForUser(userId, { id: local.resumeId, public_link_id: publicLinkId });
    }
    if (resumeHint && resumeHint.id && String(resumeHint.id) !== String(entityId)) {
      console.log('[Delete] Also tombstoning hint id:', resumeHint.id);
      addResumeTombstoneForUser(userId, { id: resumeHint.id, public_link_id: publicLinkId });
    }
    if (resumeHint && resumeHint._id && String(resumeHint._id) !== String(entityId)) {
      console.log('[Delete] Also tombstoning hint _id:', resumeHint._id);
      addResumeTombstoneForUser(userId, { id: resumeHint._id, public_link_id: publicLinkId });
    }
    if (resumeHint && resumeHint.resume_id && String(resumeHint.resume_id) !== String(entityId)) {
      console.log('[Delete] Also tombstoning hint resume_id:', resumeHint.resume_id);
      addResumeTombstoneForUser(userId, { id: resumeHint.resume_id, public_link_id: publicLinkId });
    }
    if (resumeHint && resumeHint.resumeId && String(resumeHint.resumeId) !== String(entityId)) {
      console.log('[Delete] Also tombstoning hint resumeId:', resumeHint.resumeId);
      addResumeTombstoneForUser(userId, { id: resumeHint.resumeId, public_link_id: publicLinkId });
    }

    // Remove from user-scoped local storage
    console.log('[Delete] Removing from user-scoped storage...');
    const resumes = getLocalResumesForUser(userId);
    const filtered = resumes.filter(r => {
      const match = (
        candidateIds.has(String(r.id || '')) ||
        candidateIds.has(String(r._id || '')) ||
        candidateIds.has(String(r.resume_id || '')) ||
        candidateIds.has(String(r.resumeId || '')) ||
        (publicLinkId && r.public_link_id === publicLinkId)
      );
      if (match) console.log('[Delete] Filtering out resume:', r.id || r._id);
      return !match;
    });
    console.log('[Delete] Removed', resumes.length - filtered.length, 'resume(s) from user storage');
    saveLocalResumesForUser(userId, filtered);

    // Also remove from legacy storage if present
    console.log('[Delete] Removing from legacy storage...');
    const legacy = getLegacyLocalResumes();
    const legacyFiltered = legacy.filter(r => {
      const match = (
        candidateIds.has(String(r.id || '')) ||
        candidateIds.has(String(r._id || '')) ||
        candidateIds.has(String(r.resume_id || '')) ||
        candidateIds.has(String(r.resumeId || '')) ||
        (publicLinkId && r.public_link_id === publicLinkId)
      );
      return !match;
    });
    if (legacyFiltered.length < legacy.length) {
      try {
        localStorage.setItem('localResumes', JSON.stringify(legacyFiltered));
        console.log('[Delete] Removed', legacy.length - legacyFiltered.length, 'resume(s) from legacy storage');
      } catch (err) {
        console.warn('[Delete] Could not update legacy storage:', err);
      }
    }

    console.log('[Delete] ✅ Delete successful! Resume will NOT reappear.');
    return { success: true };
  },

  /**
   * Fetch primary resume
   */
  async fetchPrimary() {
    try {
      console.log('[fetchPrimary] Starting...');
      const all = await this.fetchAll({ full: true });
      const list = Array.isArray(all) ? all : [];
      console.log('[fetchPrimary] Got', list.length, 'resumes from fetchAll (already tombstone-filtered)');

      const isPrimary = (r) => r?.is_primary === true;
      const byTs = (r) => {
        const raw = r?.updated_at ?? r?.updatedAt ?? r?.created_at ?? r?.createdAt;
        const t = raw ? Date.parse(raw) : NaN;
        return Number.isFinite(t) ? t : 0;
      };

      const primaries = list.filter(isPrimary);
      console.log('[fetchPrimary] Found', primaries.length, 'primary resumes');
      const hay = (r) => `${r?.title || ''} ${r?.target_role || ''}`.toLowerCase();

      // Prefer the seeded example if present; otherwise newest primary; otherwise newest resume.
      const preferExample = (arr) =>
        arr.find(r => hay(r).includes('senior product manager') && hay(r).includes('(example)')) || null;

      const best =
        preferExample(primaries) ||
        (primaries.sort((a, b) => byTs(b) - byTs(a))[0] || null) ||
        (preferExample(list) || null) ||
        (list.sort((a, b) => byTs(b) - byTs(a))[0] || null);

      console.log('[fetchPrimary] Selected primary resume:', best?.id || best?._id || 'none');
      return best ? [best] : [];
    } catch (error) {
      console.error('Error fetching primary resume:', error);
      throw error;
    }
  },

  /**
   * Fetch single resume by resume ID (public endpoint)
   */
  async fetchPublicResume(resumeId) {
    try {
      const rid = String(resumeId || '').trim();
      if (!rid) return null;

      // Preferred: AWS backend
      if (hasAwsBackend()) {
        const data = await awsFetchJson(`/public/resume/${encodeURIComponent(rid)}`, { auth: false });
        const it = data?.resume;
        if (!it) return null;
        
        // Extract resume content from nested 'resume' field
        const resumeObj = (it?.resume && typeof it.resume === 'object') ? it.resume : null;
        
        // Flatten: spread metadata first, then resume content (content wins)
        const flat = { ...(it || {}), ...(resumeObj || {}) };
        delete flat.resume;  // Remove nested resume object
        delete flat.resume_json;  // Remove raw JSON field if present
        
        // Ensure ID fields are preserved
        flat.id = flat.id || it?.resume_id || it?.id;
        flat._id = flat._id || it?.resume_id || it?.id;
        
        return flat;
      }

      return null;
    } catch (error) {
      console.error('Error fetching public resume:', error);
      throw error;
    }
  },

  /**
   * Fetch resume by public link ID
   */
  async fetchByPublicLink(publicLinkId) {
    try {
      // Public endpoint: do NOT apply user scoping
      const pid = String(publicLinkId || '').trim();

      // Preferred: AWS backend
      if (hasAwsBackend()) {
        const data = await awsFetchJson(`/public/profile/${encodeURIComponent(pid)}`, { auth: false });
        const profileUserId = data?.profile?.user_id || null;
        const profileSlug = data?.profile?.profile_slug || null;
        const items = Array.isArray(data?.resumes) ? data.resumes : [];
        return items.map((it) => {
          const rid = it?.resume_id || it?.resumeId || it?.id || it?._id || null;
          const resumeObj = (it?.resume && typeof it.resume === 'object') ? it.resume : null;
          
          // Spread metadata FIRST, then resume content - so content takes priority
          const flat = { ...(it || {}), ...(resumeObj || {}) };
          delete flat.resume;
          delete flat.resume_id;
          delete flat.resumeId;
          if (rid) {
            flat.id = flat.id || rid;
            flat._id = flat._id || rid;
          }
          if (profileUserId) flat.owner_user_id = flat.owner_user_id || profileUserId;
          if (profileSlug) flat.profile_slug = flat.profile_slug || profileSlug;
          flat.created_at = flat.created_at || flat.createdAt || flat.created;
          flat.updated_at = flat.updated_at || flat.updatedAt || flat.updated;
          return flat;
        });
      }

      const queryParams = new URLSearchParams({ public_link_id: pid }).toString();
      const url = `${API_BASE_URL}/me/resumes?${queryParams}`;
      const response = await fetch(url, { headers: getHeaders() });
      if (!response.ok) throw new Error(`API Error: ${response.status} ${response.statusText}`);
      const data = await response.json();
      const list = Array.isArray(data)
        ? data
        : (data && Array.isArray(data.data) ? data.data
          : (data && Array.isArray(data.results) ? data.results
            : (data && Array.isArray(data.items) ? data.items : (data ? [data] : []))));

      // Some backends ignore unknown filters and may return unrelated records.
      // Only accept records that actually match this public_link_id.
      if (!pid) return list;
      const matches = (Array.isArray(list) ? list : []).filter(r => String(r?.public_link_id || '') === pid);
      return matches.length > 0 ? matches : list;
    } catch (error) {
      console.error('Error fetching resume by public link:', error);
      throw error;
    }
  },

  /**
   * Fetch public profile by public_link_id (profile only)
   */
  async fetchPublicProfileByPublicLinkId(publicLinkId, { auth = false } = {}) {
    const pid = String(publicLinkId || '').trim();
    if (!pid) return null;
    if (hasAwsBackend()) {
      try {
        const data = await awsFetchJson(`/public/profile/${encodeURIComponent(pid)}`, { auth });
        return data?.profile || null;
      } catch {
        return null;
      }
    }
    return null;
  },

  /**
   * Fetch public profile by profile slug (profile only)
   */
  async fetchPublicProfileBySlug(profileSlug, { auth = false } = {}) {
    const slug = String(profileSlug || '').trim();
    if (!slug) return null;
    if (hasAwsBackend()) {
      try {
        const data = await awsFetchJson(`/public/profile/${encodeURIComponent(slug)}`, { auth });
        return data?.profile || null;
      } catch {
        return null;
      }
    }
    return null;
  },

  /**
   * SELECT-only diagnostics: fetch by public_link_id with HTTP status.
   * Does not throw on non-2xx.
   * For AWS backend: Returns profile data from Users table (not resumes).
   */
  async fetchByPublicLinkWithMeta(publicLinkId) {
    const pid = String(publicLinkId || '').trim();
    if (!pid) return { ok: false, status: 0, list: [] };
    
    // Preferred: AWS backend - fetch user profile from Users table ONLY (same as own profile view)
    // This matches the data source used by "View Profile" button in My Resumes section
    if (hasAwsBackend()) {
      try {
        const data = await awsFetchJson(`/public/profile/${encodeURIComponent(pid)}`, { auth: false });
        const profile = data?.profile || null;
        if (!profile) return { ok: false, status: 404, list: [] };
        
        // Helper to safely parse JSON fields
        const safeParse = (val, defaultVal = []) => {
          if (!val) return defaultVal;
          if (Array.isArray(val)) return val;
          if (typeof val === 'string') {
            try {
              const parsed = JSON.parse(val);
              return Array.isArray(parsed) ? parsed : defaultVal;
            } catch {
              return defaultVal;
            }
          }
          return defaultVal;
        };
        
        // All profile data comes from Users table ONLY (same as /me/profile endpoint)
        // This ensures Find People view matches the "View Profile" button in My Resumes
        const profileAsResume = {
          // IDs
          id: profile.user_id || profile.public_link_id,
          _id: profile.user_id || profile.public_link_id,
          user_id: profile.user_id,
          owner_user_id: profile.user_id,
          public_link_id: profile.public_link_id,
          
          // Profile fields from Users table
          title: profile.name || '',
          headline: profile.headline || '',
          summary: profile.about || profile.summary || '',
          professional_summary: profile.professionalSummary || profile.professional_summary || '',
          
          // Images from Users table
          photo_url: profile.profilePictureKey || profile.photo_url || '',
          banner_url: profile.bannerPictureKey || profile.banner_url || profile.bannerUrl || '',
          
          // Contact info from Users table
          contact_email: profile.email || profile.contact?.email || '',
          contact_phone: profile.contact?.phone || profile.phone || '',
          location: profile.location || '',
          linkedin_url: profile.linkedin_url || profile.linkedinUrl || profile.contact?.linkedin || '',
          website_url: profile.website_url || profile.websiteUrl || profile.contact?.website || '',
          
          // Profile settings from Users table
          credentials: profile.credentials || '',
          pronouns: profile.pronouns || '',
          highlight_color: profile.highlight_color || profile.highlightColor || '#3b82f6',
          profile_frame: profile.profile_frame || profile.profileFrame || '',
          target_role: profile.targetRole || profile.target_role || '',
          
          // Rich content from Users table ONLY (NOT from Resumes table)
          experience: safeParse(profile.experience || profile.work_experience || profile.workExperience, []),
          education: safeParse(profile.education || profile.educations, []),
          skills: safeParse(profile.skills || profile.skill, []),
          projects: safeParse(profile.projects || profile.project, []),
          certifications: safeParse(profile.certifications || profile.licenses || profile.license_or_certification, []),
          languages: safeParse(profile.languages || profile.language, []),
          training: safeParse(profile.training || profile.professional_development, []),
          credly_badges: safeParse(profile.credly_badges || profile.credlyBadges, []),
          core_skills: safeParse(profile.core_skills || profile.coreSkills, []),
          
          is_primary: true
        };
        
        // Normalize the profile data to match ProfilePage format
        const normalized = normalizeResumeForFullView(profileAsResume);
        
        // Debug logging
        if (import.meta?.env?.DEV) {
          console.log('[fetchByPublicLinkWithMeta] Profile from Users table:', {
            userId: profile.user_id,
            publicLinkId: profile.public_link_id,
            experienceCount: Array.isArray(normalized.experience) ? normalized.experience.length : 0,
            educationCount: Array.isArray(normalized.education) ? normalized.education.length : 0,
            hasProfessionalSummary: !!normalized.professional_summary,
            skillsCount: Array.isArray(normalized.skills) ? normalized.skills.length : 0
          });
        }
        
        return { ok: true, status: 200, list: [normalized], profile: profile };
      } catch (e) {
        return { ok: false, status: e?.status || 0, list: [], error: e?.message || 'network_error' };
      }
    }
    
    // Fallback: Base44 API
    if (!hasBase44Key()) return { ok: false, status: 0, list: [], error: base44KeyHelp };
    try {
      const queryParams = new URLSearchParams({ public_link_id: pid }).toString();
      const url = `${API_BASE_URL}/me/resumes?${queryParams}`;
      const response = await fetch(url, { headers: getHeaders() });
      const status = response.status;
      if (!response.ok) return { ok: false, status, list: [] };
      const data = await response.json();
      const list = Array.isArray(data)
        ? data
        : (data && Array.isArray(data.data) ? data.data
          : (data && Array.isArray(data.results) ? data.results
            : (data && Array.isArray(data.items) ? data.items : (data ? [data] : []))));
      const matches = (Array.isArray(list) ? list : []).filter(r => String(r?.public_link_id || '') === pid);
      return { ok: true, status, list: matches.length > 0 ? matches : list };
    } catch (e) {
      return { ok: false, status: 0, list: [], error: e?.message || 'network_error' };
    }
  },

  /**
   * Fetch public profile by slug with HTTP status (SELECT-only).
   * For AWS backend: returns profile data from Users table (and resumes fallback when authed).
   */
  async fetchPublicProfileBySlugWithMeta(profileSlug, { auth = false } = {}) {
    const slug = String(profileSlug || '').trim();
    if (!slug) return { ok: false, status: 0, list: [] };

    if (hasAwsBackend()) {
      try {
        const data = await awsFetchJson(`/public/profile/${encodeURIComponent(slug)}`, { auth });
        const profile = data?.profile || null;
        if (!profile) return { ok: false, status: 404, list: [] };
        const profileAsResume = convertPublicProfileToResumeFormat(profile);
        return { ok: true, status: 200, profile, list: profileAsResume ? [profileAsResume] : [] };
      } catch (err) {
        return { ok: false, status: err?.status || 500, list: [] };
      }
    }

    return { ok: false, status: 0, list: [] };
  },

  /**
   * ===== Published vs Draft separation =====
   *
   * - "Published" profiles are server-backed Resume records with a public_link_id.
   * - "Draft" profiles are local-only (user-scoped localStorage) and are NOT rendered on ProfilePage.
   *
   * ProfilePage must render ONLY published profiles.
   * Editing happens on ProfileEditPage (draft), with an explicit Publish action to update the server.
   */

  _pickBestPublished(list) {
    const arr = Array.isArray(list) ? list.filter(Boolean) : [];
    if (arr.length === 0) return null;
    const published = arr.filter(r => r?.public_link_id);
    if (published.length === 0) return null;
    const byTs = (r) => {
      const raw =
        r?.updated_at ??
        r?.updatedAt ??
        r?.created_at ??
        r?.createdAt ??
        r?.updated ??
        r?.created;
      const t = raw ? Date.parse(raw) : NaN;
      return Number.isFinite(t) ? t : 0;
    };
    const primaries = published.filter(r => r?.is_primary === true);
    const pool = primaries.length > 0 ? primaries : published;
    return pool.sort((a, b) => byTs(b) - byTs(a))[0] || null;
  },

  /**
   * SELECT-only: get the current user's published profile (server-backed).
   */
  async getPublishedProfileForCurrentUser() {
    const userId = getCurrentUserId();
    if (!userId) return null;
    const email = getCurrentUserSession()?.email || null;

    // 1) Preferred: scoped by user_id fields (SELECT-only).
    const scoped = await this.fetchProfileByUserId(userId, { requirePublic: true });
    const bestScoped = this._pickBestPublished(scoped);
    if (bestScoped) return bestScoped;

    // 2) Fallback: try common email fields (still SELECT-only).
    const parseList = async (response) => {
      const data = await response.json();
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.data)) return data.data;
      if (data && Array.isArray(data.results)) return data.results;
      if (data && Array.isArray(data.items)) return data.items;
      return data ? [data] : [];
    };
    const fetchWithFilters = async (filters) => {
      const query = filters ? new URLSearchParams(filters).toString() : '';
      const url = `${API_BASE_URL}/me/resumes${query ? `?${query}` : ''}`;
      const res = await fetch(url, { headers: getHeaders() });
      if (!res.ok) return [];
      return await parseList(res);
    };

    if (email) {
      const byEmail =
        (await fetchWithFilters({ contact_email: email })) ||
        [];
      const bestByEmail = this._pickBestPublished(byEmail);
      if (bestByEmail) return bestByEmail;
    }

    // 3) Last resort: fetch all and filter client-side (SELECT-only).
    // Some backends ignore unknown filters; this guarantees we can still find the published record.
    const all = await fetchWithFilters(null);
    const ownedPublished = (Array.isArray(all) ? all : [])
      .filter(r => r && r.public_link_id)
      .filter(r => {
        if (r.owner_user_id) return String(r.owner_user_id) === String(userId);
        if (r.user_id) return String(r.user_id) === String(userId);
        return !!email && resumeMatchesEmail(r, email);
      });
    return this._pickBestPublished(ownedPublished);
  },

  /**
   * SELECT-only: resolve the public_link_id for the current user's published profile.
   */
  async getPublishedPublicLinkIdForCurrentUser() {
    const pub = await this.getPublishedProfileForCurrentUser();
    return pub?.public_link_id || null;
  },

  /**
   * SELECT-only: fetch a published profile by public_link_id and pick the best record.
   */
  async fetchPublishedProfileByPublicLink(publicLinkId) {
    const pid = (publicLinkId || '').trim();
    if (!pid) return null;
    const list = await this.fetchByPublicLink(pid);
    return this._pickBestPublished(list) || (Array.isArray(list) && list.length > 0 ? list[0] : null);
  },

  /**
   * Draft (local-only): get all drafts for current user (no migrations, no server calls).
   */
  getDraftsForCurrentUser() {
    const userId = getCurrentUserId();
    if (!userId) return [];
    return getLocalResumesForUser(userId);
  },

  /**
   * Draft (local-only): get the primary/first draft for current user.
   */
  getPrimaryDraftForCurrentUser() {
    const drafts = this.getDraftsForCurrentUser();
    return drafts.find(r => r?.is_primary === true) || drafts[0] || null;
  },

  /**
   * Draft (local-only): create a new draft in localStorage.
   */
  createDraftForCurrentUser(draftData) {
    const userId = getCurrentUserId();
    if (!userId) throw new Error('Unauthorized');
    return createLocalResumeForUser(userId, {
      ...draftData,
      owner_user_id: draftData?.owner_user_id || userId
    });
  },

  /**
   * Draft (local-only): save an existing draft to localStorage.
   */
  saveDraftForCurrentUser(entityId, draftData) {
    const userId = getCurrentUserId();
    if (!userId) throw new Error('Unauthorized');
    const id = entityId || draftData?.id || draftData?._id;
    if (!id) throw new Error('Missing draft id');
    const existing = getLocalResumeByIdForUser(userId, id);
    if (existing) {
      return updateLocalResumeForUser(userId, id, { ...draftData, owner_user_id: existing.owner_user_id || userId });
    }
    return createLocalResumeForUser(userId, { ...draftData, id, _id: id, owner_user_id: userId });
  },

  /**
   * Publish action (server write):
   * - Keeps draft untouched on failure
   * - Updates/creates the server-backed published Resume for this user
   */
  async publishDraftToServer(draftData) {
    const userId = getCurrentUserId();
    if (!userId) throw new Error('Unauthorized');

    const existingPublished = await this.getPublishedProfileForCurrentUser(); // SELECT-only
    const existingId = existingPublished?.id || existingPublished?._id || null;

    const public_link_id =
      existingPublished?.public_link_id ||
      draftData?.public_link_id ||
      generatePublicLinkId();

    // Strip local-only markers and normalize payload.
    const {
      __save_status,
      __save_http_status,
      __save_error,
      ...rest
    } = (draftData || {});

    const payload = {
      ...rest,
      owner_user_id: userId,
      is_primary: true,
      public_link_id
    };

    if (existingId) {
      const res = await fetch(`${API_BASE_URL}/me/resumes/${existingId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || `Publish failed: ${res.status} ${res.statusText}`);
      }
      const data = await res.json();
      return data?.data || data?.result || data;
    }

    // No published record yet -> create server profile
    const res = await fetch(`${API_BASE_URL}/me/resumes`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message || `Publish failed: ${res.status} ${res.statusText}`);
    }
    const data = await res.json();
    return data?.data || data?.result || data;
  },

  /**
   * Resolve profile_user_id from a public_link_id (SELECT-only).
   */
  async resolveProfileUserIdFromPublicLink(publicLinkId) {
    const pid = (publicLinkId || '').trim();
    if (!pid) return null;
    const list = await this.fetchByPublicLink(pid);
    const matches = (Array.isArray(list) ? list : []).filter(r => String(r?.public_link_id || '') === String(pid));
    const r =
      (this.pickBestResumeFromList?.(matches, { includeFull: false }) || null) ||
      (Array.isArray(matches) && matches.length > 0 ? matches[0] : null) ||
      (Array.isArray(list) && list.length > 0 ? list[0] : null);
    return r?.owner_user_id || r?.ownerUserId || r?.user_id || r?.userId || null;
  },

  /**
   * Resolve profile_slug from a public_link_id (SELECT-only).
   */
  async resolveProfileSlugFromPublicLink(publicLinkId) {
    const pid = (publicLinkId || '').trim();
    if (!pid) return null;
    const profile = await this.fetchPublicProfileByPublicLinkId(pid, { auth: false });
    return profile?.profile_slug || profile?.profileSlug || null;
  },

  /**
   * Fetch profile data by profile_user_id (UUID).
   * - SELECT-only
   * - If requirePublic is true, filters to public_link_id present.
   */
  async fetchProfileByUserId(profileUserId, { requirePublic = false } = {}) {
    const uid = (profileUserId || '').trim();
    if (!uid) return [];

    // Preferred: AWS backend
    // Note: AWS backend doesn't have a direct endpoint to fetch another user's profile by user_id
    // We can only fetch by public_link_id. For user_id-based lookups, we need to use Base44 or
    // search first to get the public_link_id, then fetch by that.
    if (hasAwsBackend()) {
      // AWS backend limitation: can't fetch other users' profiles by user_id directly
      // Return empty array and let Base44 fallback handle it, or require public_link_id
      if (requirePublic) {
        // If we require public, we can't fetch without public_link_id in AWS
        return [];
      }
      // For non-public, try to use search to find the user's public_link_id first
      try {
        // This is a workaround - we'd need to search for the user to get their public_link_id
        // For now, return empty and fall through to Base44
        return [];
      } catch (err) {
        console.warn('AWS fetchProfileByUserId: user_id lookup not supported, falling back to Base44:', err);
        // Fall through to Base44
      }
    }

    const parseList = async (response) => {
      const data = await response.json();
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.data)) return data.data;
      if (data && Array.isArray(data.results)) return data.results;
      if (data && Array.isArray(data.items)) return data.items;
      return data ? [data] : [];
    };

    const tryFetch = async (filters) => {
      const query = new URLSearchParams(filters).toString();
      const url = `${API_BASE_URL}/me/resumes?${query}`;
      const res = await fetch(url, { headers: getHeaders() });
      if (!res.ok) return [];
      return await parseList(res);
    };

    // Try both common fields.
    let list = await tryFetch({ owner_user_id: uid });
    if (list.length === 0) list = await tryFetch({ user_id: uid });

    // Safety: some backends ignore unknown filters and may return unrelated records.
    // Only accept records that actually belong to the requested uid.
    const out = (Array.isArray(list) ? list : []).filter(r => {
      if (!r) return false;
      if (r.owner_user_id) return String(r.owner_user_id) === uid;
      if (r.ownerUserId) return String(r.ownerUserId) === uid;
      if (r.user_id) return String(r.user_id) === uid;
      if (r.userId) return String(r.userId) === uid;
      return false;
    });

    return requirePublic ? out.filter(r => r && r.public_link_id) : out;
  },

  /**
   * SELECT-only fallback: fetch all resumes and filter to a user_id.
   * Use only when server-side filtering isn't working for uid lookups.
   */
  async fetchProfileByUserIdFallback(profileUserId, { requirePublic = false } = {}) {
    const uid = (profileUserId || '').trim();
    if (!uid) return [];

    const res = await fetch(`${API_BASE_URL}/me/resumes`, { headers: getHeaders() });
    if (!res.ok) return [];
    const data = await res.json();
    const list = Array.isArray(data)
      ? data
      : (data?.data && Array.isArray(data.data) ? data.data
        : (data?.results && Array.isArray(data.results) ? data.results
          : (data?.items && Array.isArray(data.items) ? data.items : (data ? [data] : []))));

    const owned = (Array.isArray(list) ? list : []).filter(r => {
      if (!r) return false;
      if (r.owner_user_id) return String(r.owner_user_id) === uid;
      if (r.ownerUserId) return String(r.ownerUserId) === uid;
      if (r.user_id) return String(r.user_id) === uid;
      if (r.userId) return String(r.userId) === uid;
      return false;
    });
    return requirePublic ? owned.filter(r => r && r.public_link_id) : owned;
  },

  /**
   * Public profile fetch by profile_user_id (UUID).
   * - SELECT-only
   * - Returns ONLY publicly shareable profiles (public_link_id present)
   */
  async fetchPublicProfileByUserId(profileUserId) {
    return await this.fetchProfileByUserId(profileUserId, { requirePublic: true });
  },

  /**
   * Sync all local data (profile + resumes) to AWS backend
   * This is a one-time migration for existing local-only accounts
   */
  async syncLocalDataToAWS() {
    const fallbackApiUrl = 'https://uk8q3b8x0a.execute-api.us-east-1.amazonaws.com/dev';
    const apiBaseUrl = AWS_API_BASE_URL || fallbackApiUrl;
    
    if (!apiBaseUrl) {
      throw new Error('AWS backend not configured');
    }

    const user = getCurrentUserSession();
    if (!user?.email) {
      throw new Error('Not authenticated. Please log in first.');
    }

    const results = {
      userRegistered: false,
      profileSaved: false,
      resumesSaved: 0,
      errors: []
    };

    try {
      // Step 1: Check if user needs AWS registration
      const needsRegistration = !user?.tokens?.idToken;
      
      if (needsRegistration) {
        const password = prompt('Create a password for your app account:\n\nPassword Requirements:\n• At least 8 characters\n• At least 1 UPPERCASE letter\n• At least 1 lowercase letter\n• At least 1 number\n\nExample: MyPass123\n\nIf this is your first time, create a NEW password.\nIf you already synced before, enter your PREVIOUS password.');
        if (!password || password.length < 8) {
          throw new Error('Password must be at least 8 characters');
        }
        
        // Validate password meets requirements
        const hasUpper = /[A-Z]/.test(password);
        const hasLower = /[a-z]/.test(password);
        const hasNumber = /[0-9]/.test(password);
        
        if (!hasUpper || !hasLower || !hasNumber) {
          throw new Error('Password must contain at least one uppercase letter, one lowercase letter, and one number.\n\nExample: MyPass123');
        }

        try {
          // Try to register first
          const registerResponse = await fetch(`${apiBaseUrl}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: user.name || user.email,
              email: user.email,
              password: password
            })
          });

          let registrationSuccess = registerResponse.ok;
          let userAlreadyExists = false;

          if (!registerResponse.ok) {
            const errorData = await registerResponse.json().catch(() => ({}));
            if (errorData.error && errorData.error.includes('already exists')) {
              userAlreadyExists = true;
              results.userRegistered = 'already_exists';
            } else {
              throw new Error(`Registration failed: ${errorData.error || registerResponse.status}`);
            }
          } else {
            results.userRegistered = true;
          }

          // Login to get tokens (works whether user was just created or already existed)
          const loginResponse = await fetch(`${apiBaseUrl}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: user.email,
              password: password
            })
          });

          if (loginResponse.ok) {
            const loginData = await loginResponse.json();
            const tokens = loginData?.tokens || null;
            if (tokens?.idToken) {
              // Update local storage with AWS tokens
              const updatedUser = { ...user, tokens };
              localStorage.setItem('user', JSON.stringify(updatedUser));
            } else {
              throw new Error('Login succeeded but no tokens received');
            }
          } else {
            const loginError = await loginResponse.json().catch(() => ({}));
            throw new Error(`Login failed: ${loginError.error || 'Invalid email or password'}`);
          }
        } catch (e) {
          results.errors.push(`Registration/Login: ${e.message}`);
          throw e;
        }
      }

      // Step 2: Get local resumes
      const userId = getCurrentUserId();
      const localResumes = userId ? getLocalResumesForUser(userId) : [];

      if (localResumes.length === 0) {
        results.errors.push('No local resumes found to sync');
        return results;
      }

      // Step 3: Save primary resume to Users table (for profile display)
      const primaryResume = localResumes.find(r => r.is_primary) || localResumes[0];
      if (primaryResume) {
        try {
          await this.saveProfileToUsersTable(primaryResume);
          results.profileSaved = true;
        } catch (e) {
          results.errors.push(`Profile save: ${e.message}`);
        }
      }

      // Step 4: Save all resumes to Resumes table
      for (const resume of localResumes) {
        try {
          await awsFetchJson('/me/resumes', {
            method: 'POST',
            auth: true,
            body: {
              title: resume.title || 'My Resume',
              template: resume.template || 'modern',
              is_primary: !!resume.is_primary,
              public_link_id: resume.public_link_id || generatePublicLinkId(),
              resume: resume
            }
          });
          results.resumesSaved++;
        } catch (e) {
          results.errors.push(`Resume save (${resume.title}): ${e.message}`);
        }
      }

      return results;
    } catch (e) {
      results.errors.push(`Sync failed: ${e.message}`);
      throw e;
    }
  },

  /**
   * Save current profile data to Users table
   * This syncs the profile display to the Users table for Find People feature
   */
  async saveProfileToUsersTable(profileData) {
    const normalized = profileData || {};
    
    // Always use fallback API URL for this function if env var not set
    const fallbackApiUrl = 'https://uk8q3b8x0a.execute-api.us-east-1.amazonaws.com/dev';
    const apiBaseUrl = AWS_API_BASE_URL || fallbackApiUrl;
    
    // Temporarily override AWS_API_BASE_URL if needed to use fallback
    const originalApiBaseUrl = AWS_API_BASE_URL;
    let tempOverride = false;
    
    // If AWS_API_BASE_URL is not set, we'll use direct fetch with fallback URL
    // Otherwise, use awsFetchJson which handles auth properly
    if (AWS_API_BASE_URL) {
      // Use existing awsFetchJson helper which handles authentication
      try {
        await awsFetchJson('/me/profile', {
          method: 'PUT',
          auth: true,
          body: {
            name: normalized?.title || normalized?.name || '',
            headline: normalized?.headline || '',
            about: normalized?.summary || normalized?.about || '',
            professionalSummary: normalized?.professional_summary || normalized?.professionalSummary || '',
            location: normalized?.location || '',
            targetRole: normalized?.target_role || normalized?.targetRole || '',
            credentials: normalized?.credentials || '',
            pronouns: normalized?.pronouns || '',
            highlight_color: normalized?.highlight_color || normalized?.highlightColor || '',
            profile_frame: normalized?.profile_frame || normalized?.profileFrame || '',
            linkedin_url: normalized?.linkedin_url || normalized?.linkedinUrl || '',
            website_url: normalized?.website_url || normalized?.websiteUrl || '',
            photo_url: normalized?.photo_url || normalized?.photoUrl || '',
            banner_url: normalized?.banner_url || normalized?.bannerUrl || '',
            contact: {
              email: normalized?.contact_email || normalized?.email || '',
              phone: normalized?.contact_phone || normalized?.phone || ''
            },
            experience: Array.isArray(normalized?.experience) ? normalized.experience : [],
            education: Array.isArray(normalized?.education) ? normalized.education : [],
            skills: Array.isArray(normalized?.skills) ? normalized.skills : [],
            projects: Array.isArray(normalized?.projects) ? normalized.projects : [],
            certifications: Array.isArray(normalized?.certifications) ? normalized.certifications : [],
            languages: Array.isArray(normalized?.languages) ? normalized.languages : [],
            training: Array.isArray(normalized?.training) ? normalized.training : [],
            credly_badges: Array.isArray(normalized?.credly_badges) ? normalized.credly_badges : [],
            core_skills: Array.isArray(normalized?.core_skills) ? normalized.core_skills : [],
            ...(normalized?.profile_slug ? { profile_slug: normalized.profile_slug } : {})
          }
        });
        return { ok: true, message: 'Profile saved to Users table successfully' };
      } catch (e) {
        // If awsFetchJson fails due to auth, fall through to manual token retrieval
        if (e?.needsReauth || e?.status === 401 || e?.status === 403) {
          // Continue to manual token retrieval below
        } else {
          throw e;
        }
      }
    }
    
    // Manual token retrieval and API call (for fallback URL or when awsFetchJson fails)
    let token = getAwsIdToken();
    
    // Try additional token locations
    if (!token) {
      try {
        const user = getCurrentUserSession();
        token = user?.tokens?.idToken || user?.tokens?.IdToken || user?.idToken || user?.IdToken || '';
        
        // Also check sessionStorage
        if (!token) {
          try {
            const sessionUserStr = sessionStorage.getItem('user');
            if (sessionUserStr) {
              const sessionUser = JSON.parse(sessionUserStr);
              token = sessionUser?.tokens?.idToken || sessionUser?.tokens?.IdToken || sessionUser?.idToken || sessionUser?.IdToken || '';
            }
          } catch (e) {
            // ignore
          }
        }
        
        // Debug logging
        if (import.meta?.env?.DEV && !token) {
          console.log('[saveProfileToUsersTable] Token check:', {
            hasUser: !!user,
            hasTokens: !!user?.tokens,
            hasIdToken: !!user?.tokens?.idToken,
            hasIdTokenDirect: !!user?.idToken,
            tokenFound: !!token,
            userKeys: user ? Object.keys(user) : []
          });
        }
      } catch (e) {
        console.warn('[saveProfileToUsersTable] Error getting user session:', e);
      }
    }
    
    if (!token) {
      const user = getCurrentUserSession();
      const hasAwsBackendConfigured = !!AWS_API_BASE_URL || !!fallbackApiUrl;
      
      if (!hasAwsBackendConfigured) {
        throw new Error('AWS backend is not configured. Please set VITE_API_BASE_URL in your .env file.');
      }
      
      const isLocalAuth = user && !user?.tokens && !user?.idToken;
      
      if (isLocalAuth) {
        throw new Error('You are logged in with local authentication. To save to the Users table, please log out and log in using AWS Cognito. The login page will automatically use AWS authentication when the backend is configured.');
      }
      
      throw new Error('Authentication token not found. Please log out and log back in to refresh your session.');
    }
    
    try {
      const apiUrl = (path) => {
        const base = apiBaseUrl.replace(/\/+$/, '');
        const p = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
        return `${base}${p}`;
      };
      
      const response = await fetch(apiUrl('/me/profile'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: normalized?.title || normalized?.name || '',
          headline: normalized?.headline || '',
          about: normalized?.summary || normalized?.about || '',
          professionalSummary: normalized?.professional_summary || normalized?.professionalSummary || '',
          location: normalized?.location || '',
          targetRole: normalized?.target_role || normalized?.targetRole || '',
          credentials: normalized?.credentials || '',
          pronouns: normalized?.pronouns || '',
          highlight_color: normalized?.highlight_color || normalized?.highlightColor || '',
          profile_frame: normalized?.profile_frame || normalized?.profileFrame || '',
          linkedin_url: normalized?.linkedin_url || normalized?.linkedinUrl || '',
          website_url: normalized?.website_url || normalized?.websiteUrl || '',
          photo_url: normalized?.photo_url || normalized?.photoUrl || '',
          banner_url: normalized?.banner_url || normalized?.bannerUrl || '',
          contact: {
            email: normalized?.contact_email || normalized?.email || '',
            phone: normalized?.contact_phone || normalized?.phone || ''
          },
          experience: Array.isArray(normalized?.experience) ? normalized.experience : [],
          education: Array.isArray(normalized?.education) ? normalized.education : [],
          skills: Array.isArray(normalized?.skills) ? normalized.skills : [],
          projects: Array.isArray(normalized?.projects) ? normalized.projects : [],
          certifications: Array.isArray(normalized?.certifications) ? normalized.certifications : [],
          languages: Array.isArray(normalized?.languages) ? normalized.languages : [],
          training: Array.isArray(normalized?.training) ? normalized.training : [],
          credly_badges: Array.isArray(normalized?.credly_badges) ? normalized.credly_badges : [],
          core_skills: Array.isArray(normalized?.core_skills) ? normalized.core_skills : []
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMsg = errorData?.error || errorData?.message || `HTTP ${response.status}: ${response.statusText}`;
        
        if (response.status === 401 || response.status === 403) {
          errorMsg = 'Session expired. Please log out and log back in to refresh your authentication.';
        }
        
        throw new Error(errorMsg);
      }
      
      const data = await response.json();
      return { ok: true, message: 'Profile saved to Users table successfully', data };
    } catch (e) {
      console.error('Failed to save profile to Users table:', e);
      throw new Error(e?.message || 'Failed to save profile to Users table');
    }
  },

  /**
   * Fetch minimal user profile by user_id from Users table (for My Network).
   * Returns { title, headline, photo_url, public_link_id } or null.
   */
  async fetchUserProfileByUserId(userId) {
    const uid = (userId || '').trim();
    if (!uid) return null;
    if (!hasAwsBackend()) return null;
    try {
      const data = await awsFetchJson(`/user/${encodeURIComponent(uid)}/profile`, { auth: true });
      const p = data?.profile;
      if (!p) return null;
      return {
        title: p.name || '',
        headline: p.headline || '',
        photo_url: p.photo_url || p.profilePictureKey || '',
        public_link_id: p.public_link_id || null,
        id: p.user_id,
        _id: p.user_id,
        user_id: p.user_id,
      };
    } catch {
      return null;
    }
  },

  /**
   * SELECT-only: fetch the "best" resume record for a given user_id, using the SAME
   * selection logic as Find People (primary -> completeness -> newest).
   *
   * This is the canonical fetch for `/profile/view?uid=...`.
   */
  async fetchBestResumeForUser(profileUserId, { includeFull = true, includeNonPublic = true } = {}) {
    const uid = (profileUserId || '').trim();
    if (!uid) return null;

    const requirePublic = !includeNonPublic;
    let list = await this.fetchProfileByUserId(uid, { requirePublic });
    if (!Array.isArray(list) || list.length === 0) {
      list = await this.fetchProfileByUserIdFallback(uid, { requirePublic });
    }
    const best = pickAndMergeBestResume(list);
    if (!best) return null;

    // Return same "safe subset" shape as search results unless includeFull requested.
    const base = {
      id: best.id || best._id,
      profile_user_id: best.owner_user_id || best.ownerUserId || best.user_id || best.userId || null,
      public_link_id: best.public_link_id || null,
      title: best.title,
      headline: best.headline,
      target_role: best.target_role,
      photo_url: best.photo_url || best.photoUrl
    };
    if (!includeFull) return base;
    return normalizeResumeForFullView({ ...best, ...base });
  },

  /**
   * SELECT-only: best-effort "bring my profile back" fetch for the CURRENT user.
   * This does NOT write/migrate any data. It only attempts to locate the user's
   * best Resume record via:
   * - user_id scoped fetch (owner_user_id/user_id)
   * - if empty, email-scoped fetch (contact_email/user_email/email/etc)
   */
  async fetchBestResumeForCurrentUser({ includeFull = true } = {}) {
    const user = getCurrentUserSession();
    const userId = getCurrentUserId();
    const email = user?.email || null;

    // 1) Prefer user_id based lookup
    if (userId) {
      const bestById = await this.fetchBestResumeForUser(userId, {
        includeFull,
        includeNonPublic: true
      });
      if (bestById) return bestById;
    }

    // 2) Fallback to email-based lookup (still SELECT-only)
    const e = (email || '').trim();
    if (!e) return null;

    const parseList = async (response) => {
      const data = await response.json();
      if (Array.isArray(data)) return data;
      if (data?.data && Array.isArray(data.data)) return data.data;
      if (data?.results && Array.isArray(data.results)) return data.results;
      if (data?.items && Array.isArray(data.items)) return data.items;
      return data ? [data] : [];
    };

    const fetchWithFilters = async (filters) => {
      const query = filters ? new URLSearchParams(filters).toString() : '';
      const url = `${API_BASE_URL}/me/resumes${query ? `?${query}` : ''}`;
      const res = await fetch(url, { headers: getHeaders() });
      if (!res.ok) return [];
      return await parseList(res);
    };

    const candidates = [];
    // Try common email fields first (avoid pulling everything).
    candidates.push(...await fetchWithFilters({ contact_email: e }));
    candidates.push(...await fetchWithFilters({ contactEmail: e }));
    candidates.push(...await fetchWithFilters({ user_email: e }));
    candidates.push(...await fetchWithFilters({ userEmail: e }));
    candidates.push(...await fetchWithFilters({ email: e }));
    candidates.push(...await fetchWithFilters({ owner_email: e }));
    candidates.push(...await fetchWithFilters({ ownerEmail: e }));

    // If filters aren't supported, last resort: fetch all and filter locally.
    if (candidates.length === 0) {
      const all = await fetchWithFilters(null);
      candidates.push(...all);
    }

    const owned = candidates.filter(r => r && resumeMatchesEmail(r, e));
    const best = pickAndMergeBestResume(owned);
    if (!best) return null;

    const base = {
      id: best.id || best._id,
      profile_user_id: best.owner_user_id || best.user_id || null,
      public_link_id: best.public_link_id || null,
      title: best.title,
      headline: best.headline,
      target_role: best.target_role,
      photo_url: best.photo_url || best.photoUrl
    };
    if (!includeFull) return base;
    return normalizeResumeForFullView({ ...best, ...base });
  },

  /**
   * Public profile search (LinkedIn-like):
   * - Returns ONLY profiles that are publicly shareable (have public_link_id).
   * - Does not mutate any data.
   * - Excludes the current user from results.
   */
  async searchPublicProfiles(query, limit = 25, options = {}) {
    const qRaw = (query || '').trim();
    const q = qRaw.toLowerCase();
    if (!q) return [];
    
    // Preferred: AWS backend
    if (hasAwsBackend()) {
      try {
        const params = new URLSearchParams({ q: qRaw, limit: String(limit || 25) }).toString();
        // Search endpoint doesn't require auth, but include token if available for better results
        const hasToken = !!getAwsIdToken();
        const data = await awsFetchJson(`/search/users?${params}`, { auth: hasToken });
        const list = Array.isArray(data?.results) ? data.results : [];
        return list
          .filter(Boolean)
          .map((u) => ({
            id: u.public_link_id || u.user_id,
            profile_user_id: u.user_id || null,
            public_link_id: u.public_link_id || null,
            title: u.name || 'Unknown',
            headline: u.headline || '',
            target_role: u.target_role || '',
            photo_url: u.photo_url || u.photoUrl || '',
            location: u.location || ''
          }));
      } catch (err) {
        // If AWS API fails with auth error, retry without auth (endpoint supports both)
        if (err?.status === 401 || err?.status === 403) {
          try {
            const params = new URLSearchParams({ q: qRaw, limit: String(limit || 25) }).toString();
            const data = await awsFetchJson(`/search/users?${params}`, { auth: false });
            const list = Array.isArray(data?.results) ? data.results : [];
            return list
              .filter(Boolean)
              .map((u) => ({
                id: u.public_link_id || u.user_id,
                profile_user_id: u.user_id || null,
                public_link_id: u.public_link_id || null,
                title: u.name || 'Unknown',
                headline: u.headline || '',
                target_role: '',
                photo_url: u.photo_url || ''
              }));
          } catch (err2) {
            console.error('AWS search failed (retry without auth):', err2);
            throw new Error(`Search failed: ${err2?.message || 'Unable to connect to AWS API'}. Please check your AWS API configuration (VITE_API_BASE_URL).`);
          }
        }
        console.error('AWS search failed:', err);
        throw new Error(`Search failed: ${err?.message || 'Unable to connect to AWS API'}. Please check your AWS API configuration (VITE_API_BASE_URL).`);
      }
    }

    // Fallback to Base44 only if AWS is not configured
    if (!hasBase44Key()) {
      throw new Error(
        'Search is not configured. Please set VITE_API_BASE_URL in your environment variables ' +
        'to use AWS backend, or configure Base44 API key for legacy support.'
      );
    }

    const currentUserId = getCurrentUserId();
    const currentEmail = getCurrentUserSession()?.email || null;
    const currentEmailNorm = normalizeEmail(currentEmail);
    const qEmailNorm = normalizeEmail(qRaw);
    const isEmailLikeQuery = !!qEmailNorm && qEmailNorm.includes('@');
    const includeNonPublic = options?.includeNonPublic === true;
    const includeFull = options?.includeFull === true;
    const requirePublic = !includeNonPublic; // backwards-compatible default behavior

    // Extra-safe current-user exclusion:
    // Some older records may be missing owner_user_id/email fields. Use local (scoped) cache
    // to exclude the viewer's own ids/public links without triggering migrations.
    const ownLocal = currentUserId ? getLocalResumesForUser(currentUserId) : [];
    const ownIds = new Set(
      (ownLocal || [])
        .map(r => r?.id || r?._id)
        .filter(Boolean)
        .map(String)
    );
    const ownPublicLinks = new Set(
      (ownLocal || [])
        .map(r => r?.public_link_id)
        .filter(Boolean)
        .map(String)
    );

    const response = await fetch(`${API_BASE_URL}/me/resumes`, { headers: getHeaders() });
    if (!response.ok) throw new Error(`API Error: ${response.status} ${response.statusText}`);
    const data = await response.json();
    const list = Array.isArray(data)
      ? data
      : (data?.data && Array.isArray(data.data) ? data.data
        : (data?.results && Array.isArray(data.results) ? data.results
          : (data?.items && Array.isArray(data.items) ? data.items : (data ? [data] : []))));

    const hay = (r) => [
      r?.title || '',
      r?.headline || '',
      r?.target_role || '',
      r?.summary || '',
      r?.professional_summary || '',
      r?.location || '',
      // Allow searching by email without returning it in results
      r?.contact_email || '',
      r?.contactEmail || '',
      r?.user_email || '',
      r?.userEmail || '',
      r?.email || ''
    ].join(' ').toLowerCase();

    const isCurrentUser = (r) => {
      // Local-id/public-link based exclusion (covers missing owner_user_id/email cases)
      const rid = r?.id || r?._id;
      if (rid && ownIds.has(String(rid))) return true;
      if (r?.public_link_id && ownPublicLinks.has(String(r.public_link_id))) return true;

      if (currentUserId && (r.owner_user_id || r.ownerUserId)) return String(r.owner_user_id || r.ownerUserId) === String(currentUserId);
      if (currentUserId && (r.user_id || r.userId)) return String(r.user_id || r.userId) === String(currentUserId);
      if (currentEmailNorm) return resumeMatchesEmail(r, currentEmailNorm);
      return false;
    };

    const matchesQuery = (r) => {
      if (!r) return false;
      if (isEmailLikeQuery) {
        const emails = [
          r.contact_email,
          r.contactEmail,
          r.user_email,
          r.userEmail,
          r.email,
          r.owner_email,
          r.ownerEmail
        ].filter(Boolean).map(normalizeEmail);
        return emails.some(e => e.includes(qEmailNorm));
      }
      return hay(r).includes(q);
    };

    const results = list
      .filter(r => r && (requirePublic ? r.public_link_id : true))
      .filter(r => !isCurrentUser(r))
      .filter(matchesQuery)
      // De-dupe by user and prefer the "current" profile per user:
      // - If any is_primary exists, pick the newest primary
      // - Else pick the newest overall (by updated_at/created_at)
      .reduce((acc, r) => {
        const uid = r?.owner_user_id || r?.ownerUserId || r?.user_id || r?.userId || null;
        if (!uid) {
          // If we can't determine a user id, treat as unique by entity id.
          acc.__noUid.push(r);
          return acc;
        }
        const bucket = acc.byUid.get(uid) || [];
        bucket.push(r);
        acc.byUid.set(uid, bucket);
        return acc;
      }, { byUid: new Map(), __noUid: [] })
      // pick best from each bucket
      ;

    const pickBestForUser = (arr) => {
      return pickBestResumeRecord(arr);
    };

    // NOTE: `results` is currently the reduce accumulator object (see above).
    const deduped = [];
    for (const bucket of results.byUid.values()) {
      const best = pickBestForUser(bucket);
      if (best) deduped.push(best);
    }
    for (const r of results.__noUid) deduped.push(r);

    const final = deduped
      .sort((a, b) => {
        const p = (b?.is_primary === true) - (a?.is_primary === true);
        if (p !== 0) return p;
        const c = resumeCompletenessScore(b) - resumeCompletenessScore(a);
        if (c !== 0) return c;
        return resumeByTs(b) - resumeByTs(a);
      })
      .slice(0, limit)
      // return safe subset
      .map(r => {
        const base = {
          id: r.id || r._id,
          profile_user_id: r.owner_user_id || r.ownerUserId || r.user_id || r.userId || null,
          public_link_id: r.public_link_id || null,
          title: r.title,
          headline: r.headline,
          target_role: r.target_role,
          photo_url: r.photo_url || r.photoUrl
        };
        if (!includeFull) return base;
        return normalizeResumeForFullView({
          ...r,
          ...base,
          // Additional fields needed to render a full read-only profile if backend lookups fail.
          banner_url: r.banner_url || '',
          pronouns: r.pronouns || '',
          credentials: r.credentials || '',
          profile_frame: r.profile_frame || '',
          highlight_color: r.highlight_color || '',
          contact_email: r.contact_email || '',
          contact_phone: r.contact_phone || '',
          location: r.location || '',
          linkedin_url: r.linkedin_url || '',
          website_url: r.website_url || '',
          skills: Array.isArray(r.skills) ? r.skills : [],
          projects: Array.isArray(r.projects) ? r.projects : [],
          is_primary: r.is_primary === true,
          updated_at: r.updated_at || r.updatedAt || '',
          created_at: r.created_at || r.createdAt || ''
        });
      });

    return final;
  },

  /**
   * Delete the current user's account permanently
   * Deletes from Cognito, Users table, and Resumes table
   * Works on BOTH dev server and live site
   */
  async deleteAccount() {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    console.log('[deleteAccount] Starting account deletion...');
    console.log('[deleteAccount] Environment:', isLocalhost ? 'Development' : 'Production');
    
    // ALWAYS try AWS backend first (works on both dev and production)
    const fallbackApiUrl = 'https://uk8q3b8x0a.execute-api.us-east-1.amazonaws.com/dev';
    const apiBaseUrl = AWS_API_BASE_URL || fallbackApiUrl;
    
    // Get current user info before deletion
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('No user logged in');
    }
    
    const user = getCurrentUserSession();
    const userEmail = user?.email;
    console.log('[deleteAccount] Deleting account for:', userEmail, 'ID:', userId);

    // Get AWS token - try multiple locations
    const getAwsIdToken = () => {
      try {
        // Try user object first
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.tokens?.idToken) return u.tokens.idToken;
        if (u?.idToken) return u.idToken;
        
        // Try session storage
        const sessionUser = JSON.parse(sessionStorage.getItem('user') || '{}');
        if (sessionUser?.tokens?.idToken) return sessionUser.tokens.idToken;
        if (sessionUser?.idToken) return sessionUser.idToken;
        
        return '';
      } catch {
        return '';
      }
    };

    const token = getAwsIdToken();
    
    // If we have a token, try AWS deletion (production/AWS mode)
    if (token && apiBaseUrl) {
      try {
        console.log('[deleteAccount] Attempting AWS deletion...');
        console.log('[deleteAccount] API URL:', apiBaseUrl);
        console.log('[deleteAccount] Token present:', !!token);
        console.log('[deleteAccount] Token preview:', token ? token.substring(0, 20) + '...' : 'none');
        
        // Create AbortController for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          console.error('[deleteAccount] Request timeout after 25 seconds');
          controller.abort();
        }, 25000); // 25 second timeout
        
        let response;
        try {
          response = await fetch(`${apiBaseUrl}/me/account`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            signal: controller.signal
          });
          clearTimeout(timeoutId);
        } catch (fetchError) {
          clearTimeout(timeoutId);
          
          // Check if it's a timeout
          if (fetchError.name === 'AbortError') {
            console.error('[deleteAccount] Request timed out');
            const error = new Error('Request timed out. The server took too long to respond. Please try again.');
            error.code = 'TIMEOUT';
            throw error;
          }
          
          // Check if it's a network error
          if (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('NetworkError')) {
            console.error('[deleteAccount] Network error:', fetchError.message);
            const error = new Error('Network error: Unable to connect to the server. Please check your internet connection and try again.');
            error.code = 'NETWORK_ERROR';
            error.originalError = fetchError.message;
            throw error;
          }
          
          throw fetchError;
        }

        console.log('[deleteAccount] Response status:', response.status);
        console.log('[deleteAccount] Response headers:', Object.fromEntries(response.headers.entries()));
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('[deleteAccount] Server error response:', errorData);
          
          const errorMsg = errorData.message || errorData.error || `Server returned ${response.status}`;
          const error = new Error(errorMsg);
          error.code = errorData.code || `HTTP_${response.status}`;
          error.statusCode = response.status;
          throw error;
        }
        
        const result = await response.json().catch(() => ({}));
        console.log('[deleteAccount] ✅ Account deleted from AWS:', result);
        
        // Clear only THIS user's data from browser
        console.log('[deleteAccount] Clearing local data for user:', userId);
        const allKeys = Object.keys(localStorage);
        let clearedKeys = 0;
        for (const key of allKeys) {
          // Delete user-specific resume data
          if (key.startsWith(`resume_${userId}_`) || key === `resumes_${userId}`) {
            localStorage.removeItem(key);
            clearedKeys++;
          }
        }
        console.log('[deleteAccount] Cleared', clearedKeys, 'localStorage keys');
        
        // Clear session data and current user
        sessionStorage.clear();
        localStorage.removeItem('user');
        
        return { 
          success: true, 
          mode: 'aws', 
          message: result.message || 'Account permanently deleted from AWS',
          deletedResumes: result.deletedResumes,
          email: result.email
        };
      } catch (error) {
        console.error('[deleteAccount] ❌ AWS deletion failed:', error);
        console.error('[deleteAccount] Error details:', {
          message: error.message,
          code: error.code,
          statusCode: error.statusCode,
          name: error.name
        });
        
        // If we're in localhost, fall back to local deletion
        if (isLocalhost) {
          console.log('[deleteAccount] Falling back to local deletion...');
        } else {
          // On production, don't fall back - throw the error
          throw error;
        }
      }
    } else {
      console.warn('[deleteAccount] No token or API URL available');
      console.warn('[deleteAccount] Token:', token ? 'present' : 'missing');
      console.warn('[deleteAccount] API URL:', apiBaseUrl || 'missing');
      
      if (!isLocalhost) {
        throw new Error('Cannot delete account: Authentication token not found. Please try logging out and back in.');
      }
    }
    
    // Local-only deletion (dev server without AWS token)
    if (isLocalhost) {
      console.log('[deleteAccount] Running local deletion (dev mode)...');
      
      // Delete only resumes for this specific user
      const allKeys = Object.keys(localStorage);
      for (const key of allKeys) {
        // Delete user-specific resume data
        if (key.startsWith(`resume_${userId}_`) || key === `resumes_${userId}`) {
          localStorage.removeItem(key);
        }
      }
      
      // Clear session data
      sessionStorage.clear();
      
      // Remove current user from localStorage
      localStorage.removeItem('user');
      
      console.log('[deleteAccount] ✅ Local data cleared');
      return { success: true, mode: 'local', message: 'Account data cleared from browser (dev mode)' };
    }
    
    // If we reach here, we're on production without a token
    throw new Error('Authentication required. Please log out and log back in to delete your account.');
  },

  // Fetch imported DOCX HTML from S3
  fetchImportedHtml: async (resumeId) => {
    const token = getAuthToken();
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || AWS_API_BASE_URL;
    if (!token || !apiBaseUrl) throw new Error('Authentication required.');
    const response = await fetch(`${apiBaseUrl}/me/resumes/${resumeId}/imported-html`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `Failed to fetch imported HTML: ${response.status}`);
    }
    const data = await response.json();
    return data.html;
  },

  // Render JSON Resume template (server-side rendering)
  renderTemplate: async (resumeId, templateId) => {
    console.log('[renderTemplate] Rendering template:', { resumeId, templateId });
    
    const token = getAuthToken();
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || AWS_API_BASE_URL;
    
    if (!token || !apiBaseUrl) {
      console.error('[renderTemplate] Missing auth:', { hasToken: !!token, hasApiUrl: !!apiBaseUrl });
      throw new Error('Authentication required. Please log in.');
    }
    
    try {
      let response;
      try {
        response = await fetch(`${apiBaseUrl}/me/resumes/${resumeId}/render-template`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ templateId })
        });
      } catch (networkErr) {
        console.error('[renderTemplate] Network error:', networkErr);
        throw new Error('Network error: Unable to reach template rendering service. Please check your connection and try again.');
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to render template: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('[renderTemplate] Template rendered successfully');
      return data.html;
    } catch (error) {
      console.error('[renderTemplate] Error:', error);
      throw error;
    }
  },
};

// Generate a unique public link ID
export const generatePublicLinkId = () => {
  return Math.random().toString(36).substring(2, 15) +
         Math.random().toString(36).substring(2, 15);
};

export default resumeAPI;
